// progression.js — the progression + periodization engine. Pure functions, no
// React, no I/O, no Date.now() unless a caller omits `now`.
//
// Every program exercise (pe) runs one scheme, stored on pe.scheme:
//
//   'double'      (default) reps first, then weight: all sets at the top of the
//                 range → +1 step. Identical to session_model.applyProgression.
//   'linear'      fixed sets × reps; add cfg.inc every successful session. After
//                 cfg.failLimit (default 3) consecutive misses, drop 10 % and
//                 build back. Linked: every linear slot of the same exercise in
//                 the program moves together (A/B novice programs squat every day).
//   'percent'     % of a training max (TM). cfg.wave names a table in WAVES (or
//                 cfg.weeks is an inline table). Supports 5/3/1 waves with "+"
//                 (AMRAP) sets, supplemental BBB/FSL work, pyramid T1/T2 days,
//                 Texas-style volume/recovery/intensity days, generic % blocks
//                 and peaking. How the TM moves is cfg.tmRule ?? wave.tmRule:
//                   'cycle'   bump once per wave on the bump week if the + set
//                             made its minimum; missed → TM −10 %
//                   'amrap'   bump every session from the + set (pyramid style)
//                   'rtf'     adjust every session by reps vs the rep-out target
//                   'success' +inc when every set is made; misses → −10 %
//                   'test'    on the wave's test week, TM = 90 % of the best e1RM
//                   'none'
//                 TMs live in program.tms[tmKey] (lb). tmKey = cfg.tmKey (another
//                 lift's exercise id, e.g. close-grip bench off the bench TM) or
//                 pe.exerciseId; pe.tm is honoured as a fallback.
//   'rpe_topset'  one top set (cfg.topReps) at the week's target RPE, then
//                 cfg.backoffs sets of cfg.backoffReps at cfg.drop % below the top
//                 load implied by TODAY's e1RM. The e1RM is carried in
//                 pe.schemeState.e1rm and re-estimated every session.
//   'gzcl'        tiered: T1 5×3+ → 6×2+ → 10×1+ (fail moves a stage, failing 10×1
//                 resets to 5×3+ at 85 % of an estimated 5RM); T2 3×10 → 3×8 → 3×6
//                 (failing 3×6 resets to 3×10 at the last 3×10 weight + 2 steps);
//                 T3 3×15+ adds weight when the + set reaches 25.
//   'rp_sets'     hypertrophy mesocycle: RIR target ramps 3→0 across the
//                 accumulation weeks, +1 set a week from cfg.mev up to cfg.mrv,
//                 a deload week at the end. Missing reps on half the sets holds
//                 the set count (recovery-limited); load uses double progression.
//
// Weeks: program.cycle = { weeks: [{ label, deload? }], repeat: true|false }.
// programWeek() derives the absolute week index from completed sessions, so
// the plan advances by itself: a training week is one rotation through the
// program's days, whatever the calendar dates (Sat/Sun/Mon training, or a day
// slipping past Monday, never skips a lift's week). It closes when every day
// is done (or explicitly skipped with skipProgramDay), or when the user starts
// a day they already did this rotation — the days they missed are reported in
// programWeekInfo().skipped, never dropped silently. program.weekRule ===
// 'calendar' opts into fixed-weekday weeks (closes on a new calendar week once
// half the days were done). weekInfo(program, index) maps it into the cycle.
//
// Units: everything is stored in lb. Loads are rounded to what can actually be
// loaded in the user's units — 5 lb / 2.5 lb, or 2.5 kg / 1.25 kg — so a kg
// user never sees 61.2 kg.

// API (all pure):
//   prescribe({ program, pe, weekIndex, history, units, sessionSets }) → { rows, note, week, ... }
//   afterSession({ program, session, slots, units, weekIndex? })     → { program, changes, progressed }
//   programWeek({ program, sessions, now }) → absolute week index; programWeekInfo(...) → + cycle info
//   nextProgramDay({ program, sessions, now }) → { day, index, week }  (new program → Day 1)
//   planSuggestion({ slot, workIdx, loggedSets, units }) → live { w, reps, tier:'plan', note } | null
//   schemeLabel(pe, units), rowsSummary(rows, units), weekTable(program, pe, units)
//   tmLifts / missingTMs / withTrainingMaxes / trainingMaxFrom, withCurrentWeek, restartPlan

import { e1rm, e1rmRPE, pctOf1RM, weekStart, exerciseKind } from './analytics.js';

// Rows that are not working sets of their own: warm-ups, drop sets and the
// mini-sets after a myo / rest-pause / cluster activation set (the activation
// set itself is a working set).
const NOT_WORK = new Set(['warmup', 'drop', 'mini']);
const isWorkType = (t) => !NOT_WORK.has(t);
// Assisted lifts log the assistance: progress means LESS of it.
const isAssisted = (pe) => !!pe && exerciseKind({ name: pe.name, equipment: pe.equipment, load: pe.load }).load === 'assisted';

export const KG = 0.45359237;

// ——— Scheme vocabulary ————————————————————————————————————————————————————

export const SCHEMES = {
  double:     { id: 'double',     label: 'Double progression', short: 'Reps, then weight' },
  linear:     { id: 'linear',     label: 'Linear',             short: 'Add weight every session' },
  percent:    { id: 'percent',    label: 'Training max %',     short: 'Percentages of a training max' },
  rpe_topset: { id: 'rpe_topset', label: 'Top set + back-offs', short: 'RPE top set, back-offs from today\'s e1RM' },
  gzcl:       { id: 'gzcl',       label: 'Tiered (T1/T2/T3)',  short: 'Stage changes on a missed rep' },
  rp_sets:    { id: 'rp_sets',    label: 'Hypertrophy meso',   short: 'RIR 3→0, +1 set a week, deload' },
};
export const schemeOf = (pe) => (pe && SCHEMES[pe.scheme]) ? pe.scheme : 'double';

// ——— Waves (percent tables) ——————————————————————————————————————————————
// A wave is an array of weeks; a week is an array of set specs
//   { pct (of TM), reps, sets? (repeat, default 1), amrap?, target? (rep-out target) }
// Optional: tmRule, bumpWeek (for 'cycle'), testWeek (for 'test'), deloadWeeks.

const S = (pct, reps, extra = {}) => ({ pct, reps, ...extra });
const X = (n, pct, reps, extra = {}) => ({ pct, reps, sets: n, ...extra });
const A = (pct, reps, extra = {}) => ({ pct, reps, amrap: true, ...extra });

export const WAVES = {
  // Four-week 5/3/1-style wave (main lift).
  w531: {
    label: '5s / 3s / 5-3-1 / deload', tmRule: 'cycle', bumpWeek: 2, deloadWeeks: [3],
    weeks: [
      [S(65, 5), S(75, 5), A(85, 5)],
      [S(70, 3), S(80, 3), A(90, 3)],
      [S(75, 5), S(85, 3), A(95, 1)],
      [S(40, 5), S(50, 5), S(60, 5)],
    ],
  },
  // Supplemental: big volume 5×10 (rides the main lift's TM, never bumps it).
  bbb: {
    label: '5×10 supplemental', tmRule: 'none', deloadWeeks: [3],
    weeks: [[X(5, 50, 10)], [X(5, 55, 10)], [X(5, 60, 10)], [X(3, 40, 5)]],
  },
  // Supplemental: first set last, 5×5 at the week's first-set percentage.
  fsl: {
    label: 'First set last 5×5', tmRule: 'none', deloadWeeks: [3],
    weeks: [[X(5, 65, 5)], [X(5, 70, 5)], [X(5, 75, 5)], [X(3, 40, 5)]],
  },
  // Pyramid T1 (nine sets, heavy single + back-down AMRAP), one-week wave.
  pyrBench: {
    label: 'Volume pyramid (8s)', tmRule: 'none',
    weeks: [[S(65, 8), S(75, 6), X(3, 85, 4), S(80, 5), S(75, 6), S(70, 7), A(65, 8)]],
  },
  pyrHeavy: {
    label: 'Heavy pyramid (1+)', tmRule: 'amrap',
    weeks: [[S(75, 5), S(85, 3), A(95, 1), S(90, 3), S(85, 3), S(80, 3), S(75, 5), S(70, 5), A(65, 5)]],
  },
  pyrT2: {
    label: 'T2 pyramid (70 %)', tmRule: 'none',
    weeks: [[S(50, 6), S(60, 5), S(70, 3), S(70, 5), S(70, 7), S(70, 4), S(70, 6), S(70, 8)]],
  },
  pyrT2light: {
    label: 'T2 pyramid (60 %)', tmRule: 'none',
    weeks: [[S(40, 6), S(50, 5), S(60, 3), S(60, 5), S(60, 7), S(60, 4), S(60, 6), S(60, 8)]],
  },
  pyrT2front: {
    label: 'T2 pyramid (55 %)', tmRule: 'none',
    weeks: [[S(35, 5), S(45, 5), S(55, 3), S(55, 5), S(55, 7), S(55, 4), S(55, 6), S(55, 8)]],
  },
  // Texas-style week: volume / recovery / intensity days (TM ≈ 5RM-ish).
  txVolume:    { label: 'Volume day 5×5',   tmRule: 'none',    weeks: [[X(5, 85, 5)]] },
  txRecovery:  { label: 'Recovery day 2×5', tmRule: 'none',    weeks: [[X(2, 70, 5)]] },
  txIntensity: { label: 'Intensity day 1×5', tmRule: 'success', weeks: [[S(95, 5)]] },
  // Six-week strength block (conditioning → heavy → test).
  block6: {
    label: '6-week strength block', tmRule: 'test', testWeek: 5,
    weeks: [
      [X(4, 75, 6)],
      [X(3, 80, 5), A(80, 5)],
      [X(4, 87.5, 3), A(87.5, 3)],
      [X(4, 92.5, 2)],
      [X(2, 97.5, 2), S(102.5, 1)],
      [S(85, 3), S(95, 1), A(105, 1)],
    ],
  },
  // Twelve-week peaking block: volume → strength → peak → taper and test.
  peak12: {
    label: '12-week peak', tmRule: 'test', testWeek: 11, deloadWeeks: [3, 7, 10],
    weeks: [
      [X(4, 65, 8)], [X(4, 67.5, 8)], [X(5, 72.5, 6)], [X(3, 60, 5)],
      [X(5, 77.5, 5)], [X(5, 80, 4)], [X(4, 85, 3)], [X(3, 65, 3)],
      [X(4, 90, 2)], [X(3, 95, 1), S(100, 1)], [X(2, 80, 2)],
      [S(90, 1), S(100, 1), A(108, 1)],
    ],
  },
  // Eight-week meet peak: volume → strength → deload → heavy → peak → taper → test.
  peak8: {
    label: '8-week peak', tmRule: 'test', testWeek: 7, deloadWeeks: [3, 6],
    weeks: [
      [X(4, 70, 6)], [X(4, 75, 5)], [X(5, 80, 4)], [X(3, 60, 5)],
      [X(4, 85, 3)], [X(3, 90, 2), S(95, 1)], [X(2, 75, 2)],
      [S(90, 1), S(97.5, 1), A(104, 1)],
    ],
  },
  // Rep-out hypertrophy (RTF/SBS-style): 4 sets, last set to the rep-out target.
  rtf: {
    label: 'Rep-out wave', tmRule: 'rtf', deloadWeeks: [6],
    weeks: [
      [X(3, 70, 10), A(70, 10, { target: 12 })],
      [X(3, 72.5, 9), A(72.5, 9, { target: 11 })],
      [X(3, 75, 8), A(75, 8, { target: 10 })],
      [X(3, 72.5, 9), A(72.5, 9, { target: 11 })],
      [X(3, 75, 8), A(75, 8, { target: 10 })],
      [X(3, 77.5, 7), A(77.5, 7, { target: 9 })],
      [X(3, 60, 5)],
    ],
  },
  // Daily undulating: heavy / medium / light days (heavy day drives the TM).
  dupHeavy:  { label: 'Heavy 5×3',  tmRule: 'rtf',  weeks: [[X(4, 85, 3), A(85, 3, { target: 6 })]] },
  dupMedium: { label: 'Medium 4×6', tmRule: 'none', weeks: [[X(4, 75, 6)]] },
  dupLight:  { label: 'Light 3×8',  tmRule: 'none', weeks: [[X(3, 65, 8)]] },
  // Bench specialization: 3 exposures, 6-week ramp then test.
  benchHeavy: {
    label: 'Heavy bench ramp', tmRule: 'test', testWeek: 5,
    weeks: [[X(5, 77.5, 5)], [X(5, 80, 4)], [X(5, 85, 3)], [X(4, 90, 2)], [X(3, 95, 1)], [S(85, 2), S(95, 1), A(105, 1)]],
  },
  benchVolume: {
    label: 'Volume bench ramp', tmRule: 'none',
    weeks: [[X(4, 67.5, 8)], [X(4, 70, 8)], [X(4, 72.5, 7)], [X(4, 75, 6)], [X(3, 70, 5)], [X(2, 60, 5)]],
  },
};

// ——— Units and rounding ——————————————————————————————————————————————————

const round2 = (x) => Math.round(x * 100) / 100;

// Smallest sensible jump in lb for the user's units.
export function plateStep(units = 'lb', small = false) {
  if (units === 'kg') return (small ? 1.25 : 2.5) / KG;
  return small ? 2.5 : 5;
}

// Round a lb weight to a loadable value in the user's units, returned in lb.
export function roundTo(lb, units = 'lb', small = false) {
  if (!(lb > 0)) return 0;
  if (units === 'kg') {
    const step = small ? 1.25 : 2.5;
    const kg = Math.max(step, Math.round((lb * KG) / step) * step);
    return round2(kg / KG);
  }
  const step = small ? 2.5 : 5;
  return Math.max(step, Math.round(lb / step) * step);
}

// ——— Loading: the user's own bar, plates and dumbbells ————————————————————
// prefs (state.prefs): { units, barWeight (lb), plates: { lb: [...], kg: [...] },
// dbStep? (dumbbell jump in display units: 5 lb / 2.5 kg by default) }.
// A barbell load is the bar plus two equal stacks of plates the user owns, so
// a 42.5 lb press on a 45 lb bar becomes the bar, and 52.5 needs 1.25s.

export const DEFAULT_PLATES = { lb: [45, 35, 25, 10, 5, 2.5], kg: [25, 20, 15, 10, 5, 2.5, 1.25] };
export const DEFAULT_DB_STEP = { lb: 5, kg: 2.5 };

// How a program exercise is loaded: 'barbell' | 'dumbbell' | 'kettlebell' |
// 'stack' (machine, cable, smith) | 'other' (bodyweight, band) | null (unknown).
export function equipmentKind(pe, exercises = null) {
  if (!pe) return null;
  const ex = Array.isArray(exercises) && pe.exerciseId ? exercises.find(e => e && e.id === pe.exerciseId) : null;
  const eq = String(pe.equipment || ex?.equipment || '');
  const name = `${pe.name || ''} ${ex?.name || ''}`;
  if (/^barbell$|^trap bar$/i.test(eq)) return 'barbell';
  if (/dumbbell/i.test(eq)) return 'dumbbell';
  if (/kettlebell/i.test(eq)) return 'kettlebell';
  if (/machine|cable|smith|lever|ez/i.test(eq)) return 'stack';
  if (/bodyweight|band|other/i.test(eq)) return 'other';
  if (/dumbbell|\bdb\b/i.test(name)) return 'dumbbell';
  if (/kettlebell|\bkb\b/i.test(name)) return 'kettlebell';
  if (/machine|cable|smith|pulldown|leg press|hack squat|pec deck|leg curl|leg extension|pushdown|lat pull|ez[- ]?bar/i.test(name)) return 'stack';
  if (/barbell|back squat|front squat|deadlift|bench press|overhead press|military press|push press|power clean|pendlay|good morning|hip thrust|box squat|pause squat|floor press|pin press|rack pull/i.test(name)) return 'barbell';
  return null;
}

const barOf = (prefs, units) => {
  const kg = units === 'kg';
  const lb = prefs?.barWeight > 0 ? prefs.barWeight : (kg ? 20 / KG : 45);
  return kg ? Math.round(lb * KG * 100) / 100 : lb;
};
// Per-side plate totals the user can build (display units), ascending. Any
// number of each plate size; cached per plate set.
const _sides = new Map();
function sideTotals(plates, units) {
  const list = (Array.isArray(plates) && plates.filter(p => p > 0).length ? plates : DEFAULT_PLATES[units === 'kg' ? 'kg' : 'lb']).filter(p => p > 0);
  const key = units + ':' + list.slice().sort((a, b) => a - b).join(',');
  if (_sides.has(key)) return _sides.get(key);
  const cents = list.map(p => Math.round(p * 100));
  const g = cents.reduce((a, b) => { while (b) [a, b] = [b, a % b]; return a; });
  const max = Math.round((units === 'kg' ? 300 : 650) * 100 / g);
  const ok = new Uint8Array(max + 1); ok[0] = 1;
  const steps = cents.map(c => c / g);
  for (let i = 1; i <= max; i++) for (const s of steps) if (i >= s && ok[i - s]) { ok[i] = 1; break; }
  const out = [];
  for (let i = 0; i <= max; i++) if (ok[i]) out.push(i * g / 100);
  _sides.set(key, out);
  return out;
}

// Round a lb load to what this lifter can actually load; returns lb.
// dir: 0 nearest (ties go up), -1 at or below, +1 at or above.
export function loadable(lb, { kind = null, prefs = null, units = 'lb', small = false, dir = 0 } = {}) {
  if (!(lb > 0)) return 0;
  const kg = units === 'kg';
  const toLb = (v) => (kg ? round2(v / KG) : Math.round(v * 100) / 100);
  const v = kg ? lb * KG : lb;
  const pick = (cands, x) => {
    if (dir < 0) { let b = cands[0]; for (const c of cands) { if (c <= x + 1e-6) b = c; else break; } return b; }
    if (dir > 0) { for (const c of cands) if (c >= x - 1e-6) return c; return cands[cands.length - 1]; }
    let b = cands[0];
    for (const c of cands) {
      const d = Math.abs(c - x), db = Math.abs(b - x);
      if (d < db - 1e-9 || (Math.abs(d - db) <= 1e-9 && c > b)) b = c;
      else if (c > x) break;
    }
    return b;
  };
  if (kind === 'barbell') {
    const bar = barOf(prefs, units);
    if (v <= bar + 1e-6) return toLb(bar);
    const sides = sideTotals(prefs?.plates?.[kg ? 'kg' : 'lb'], units);
    const side = pick(sides, (v - bar) / 2);
    return toLb(bar + 2 * side);
  }
  if (kind === 'dumbbell' || kind === 'kettlebell') {
    const step = kind === 'kettlebell' ? (kg ? 4 : 5) : (prefs?.dbStep > 0 ? prefs.dbStep : DEFAULT_DB_STEP[kg ? 'kg' : 'lb']);
    const n = dir < 0 ? Math.floor(v / step + 1e-9) : dir > 0 ? Math.ceil(v / step - 1e-9) : Math.round(v / step);
    return toLb(Math.max(step, n * step));
  }
  if (!dir) return roundTo(lb, units, small);
  const step = kg ? (small ? 1.25 : 2.5) : (small ? 2.5 : 5);
  const n = dir < 0 ? Math.floor(v / step + 1e-9) : Math.ceil(v / step - 1e-9);
  return toLb(Math.max(step, n * step));
}
// The next loadable weight at least `inc` above `from` (never the same weight).
export function loadUp(from, inc, opts = {}) {
  let to = loadable(from + inc, opts);
  if (!(to > from + 1e-6)) to = loadable(from + 1e-3, { ...opts, dir: 1 });
  return to;
}

// A nominal lb increment (5, 10, 2.5) expressed in the user's units (lb).
export function incFor(lbInc, units = 'lb') {
  if (units !== 'kg') return lbInc;
  const kg = lbInc >= 10 ? 5 : lbInc >= 5 ? 2.5 : 1.25;
  return kg / KG;
}

const SMALL_GROUPS = new Set(['Shoulders', 'Biceps', 'Triceps', 'Core', 'Forearms', 'Calves']);
export const isSmall = (pe) => !!(pe && (pe.schemeCfg?.small ?? SMALL_GROUPS.has(pe.group)));
const defaultInc = (pe) => pe?.schemeCfg?.inc ?? (isSmall(pe) ? 2.5 : 5);

const fmtW = (lb, units = 'lb') => {
  if (!(lb > 0)) return '0';
  const v = units === 'kg' ? lb * KG : lb;
  return String(Math.round(v * 10) / 10);
};
const U = (units) => (units === 'kg' ? 'kg' : 'lb');

// ——— Weeks / cycles ——————————————————————————————————————————————————————

export function cycleOf(program) {
  const c = program?.cycle;
  if (c && Array.isArray(c.weeks) && c.weeks.length) return { weeks: c.weeks, repeat: c.repeat !== false };
  const per = program?.periodization?.weeks;
  if (Array.isArray(per) && per.length) {
    return { weeks: per.map((w, i) => ({ label: w.label || `Week ${i + 1}`, deload: !!w.deload || /deload/i.test(w.label || '') })), repeat: false };
  }
  return { weeks: [{ label: 'Week' }], repeat: true };
}

// Map an absolute week index into the program's cycle.
export function weekInfo(program, index = 0) {
  const { weeks, repeat } = cycleOf(program);
  const n = weeks.length;
  const abs = Math.max(0, index | 0);
  const week = repeat ? abs % n : Math.min(abs, n - 1);
  const cycle = repeat ? Math.floor(abs / n) : 0;
  const w = weeks[week] || {};
  return {
    index: abs, week, cycle, length: n, repeat,
    label: w.label || `Week ${week + 1}`,
    deload: !!w.deload,
    finished: !repeat && abs >= n, // a fixed block has run out — time to pick the next one
  };
}

// Absolute week index from completed sessions. See the header for the rule.
export function programWeek({ program, sessions = [], now = Date.now() } = {}) {
  return programWeekInfo({ program, sessions, now }).index;
}

export function programWeekInfo({ program, sessions = [], now = Date.now() } = {}) {
  if (!program) return { ...weekInfo(null, 0), daysDone: [], daysTotal: 0, skipped: [] };
  const dayIds = (program.days || []).map(d => d.id);
  const valid = new Set(dayIds);
  const total = dayIds.length || 1;
  const since = program.startedAt || 0;
  // Events in time order: completed sessions of this program, plus days the
  // user explicitly skipped ("Skip this day"), which count as done.
  const events = sessions
    .filter(s => s && s.programId === program.id && s.completedAt && (s.startedAt || s.completedAt) >= since)
    .map(s => ({ ts: s.startedAt || s.completedAt, dayId: s.dayId }))
    .concat((program.skips || []).filter(k => k && k.at >= since).map(k => ({ ts: k.at, dayId: k.dayId, skip: true })))
    .filter(e => valid.has(e.dayId) || !dayIds.length)
    .sort((a, b) => a.ts - b.ts);
  const calendar = program.weekRule === 'calendar';
  const minDays = Math.max(1, Math.ceil(total / 2));
  let idx = program.weekOffset || 0;
  let days = new Set();
  let skippedNow = new Set();
  let cal = null;
  let skipped = []; // [{ week, dayIds }] — days a closed week never did (never silent)
  const close = () => {
    const missing = dayIds.filter(d => !days.has(d));
    if (missing.length) skipped.push({ week: idx, dayIds: missing });
    idx++; days = new Set(); skippedNow = new Set(); cal = null;
  };
  for (const e of events) {
    // A week (one rotation through the program's days) ends when every day is
    // done, whatever the calendar says. Starting a day that was already done
    // this rotation is the user starting the next rotation; the days they
    // never did are reported in `skipped`, not dropped silently.
    if (days.size >= total) close();
    else if (days.size > 0 && days.has(e.dayId) && !e.skip) close();
    // Opt-in fixed-weekday mode: a new calendar week closes the old one once
    // at least half of its days were trained.
    else if (calendar && days.size >= minDays && cal != null && weekStart(e.ts) !== cal) close();
    if (cal == null) cal = weekStart(e.ts);
    days.add(e.dayId);
    if (e.skip) skippedNow.add(e.dayId);
  }
  if (days.size >= total) close();
  else if (calendar && days.size >= minDays && cal != null && weekStart(now) !== cal) close();
  return { ...weekInfo(program, idx), daysDone: [...days], daysSkipped: [...skippedNow], daysTotal: total, skipped };
}

// The next workout in program order: the first day not yet done in the current
// rotation. A brand-new program always starts at Day 1, whatever the weekday;
// a day is never skipped unless the user skips it (skipProgramDay).
// → { day, index, week } (day null if no days).
export function nextProgramDay({ program, sessions = [], now = Date.now() } = {}) {
  const days = program?.days || [];
  const info = programWeekInfo({ program, sessions, now });
  if (!days.length) return { day: null, index: -1, week: info };
  const done = new Set(info.daysDone);
  const i = days.findIndex(d => !done.has(d.id));
  const index = i < 0 ? 0 : i;
  return { day: days[index], index, week: info };
}

// ——— nextWorkout: the one answer to "what's next" in a program run ——————————
//
// USAGE (Home hero, "Skip <day>", week label, calendar, "not done" badges — all
// of them read this, never their own weekday search):
//   const nx = nextWorkout(state, state.activeProgramId);   // or (state) for the active one
//   if (nx.day) {
//     title    = nx.day.name                    // what to train (rotation order)
//     eyebrow  = nx.today ? 'Today' : 'Up next' // the weekday is a suggestion of *when*
//     subline  = nx.reason                      // "Week 1 · 5s · planned for Wed — Press and Deadlift done"
//     week     = nx.weekLabel, nx.deload, nx.weekIndex (absolute) / nx.week (in the cycle)
//     skip     = actions.updateProgram(id, { skips: skipProgramDay(program, nx.day.id).skips })
//     badges   = nx.doneDayIds / nx.skippedDayIds (this rotation), nx.missedDays
//                (days the rotation already moved past without them; "not done", never
//                a day that was simply trained on another weekday)
//   }
//   nx.active → the live session of this program (resume it instead), nx.trainedToday.
// Pure: pass `now` for tests.
export function nextWorkout(state, programId = null, now = Date.now()) {
  const programs = state?.programs || [];
  const program = programs.find(p => p.id === (programId || state?.activeProgramId)) || null;
  const empty = { program: null, day: null, index: -1, weekIndex: 0, week: 0, weekLabel: '', deload: false, cycle: 0, finished: false, hasWeeks: false, today: false, plannedFor: '', inDays: null, doneDayIds: [], skippedDayIds: [], missedDays: [], active: null, trainedToday: false, reason: '' };
  if (!program) return empty;
  const sessions = state?.sessions || [];
  const info = programWeekInfo({ program, sessions, now });
  const days = program.days || [];
  const done = new Set(info.daysDone);
  const i = days.findIndex(d => !done.has(d.id));
  const index = days.length ? (i < 0 ? 0 : i) : -1;
  const day = index >= 0 ? days[index] : null;
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayDow = DOW[new Date(now).getDay()];
  const inDays = day?.day && DOW.includes(day.day) ? (DOW.indexOf(day.day) - DOW.indexOf(todayDow) + 7) % 7 : null;
  const live = state?.activeSessionId ? sessions.find(s => s.id === state.activeSessionId && !s.completedAt && s.programId === program.id) || null : null;
  const dayStart = new Date(now); dayStart.setHours(0, 0, 0, 0);
  const trainedToday = sessions.some(s => s.programId === program.id && s.completedAt && (s.startedAt || s.completedAt) >= dayStart.getTime());
  const prevWeek = info.skipped.filter(k => k.week === info.index - 1).flatMap(k => k.dayIds);
  const missedDays = prevWeek.map(id => days.find(d => d.id === id)).filter(Boolean).map(d => ({ dayId: d.id, name: d.name }));
  const hasWeeks = !!program.cycle || cycleOf(program).weeks.length > 1;
  const nameOf = (id) => days.find(d => d.id === id)?.name;
  const doneNames = info.daysDone.filter(id => !info.daysSkipped.includes(id)).map(nameOf).filter(Boolean);
  const list = (a) => (a.length <= 2 ? a.join(' and ') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
  const parts = [];
  if (hasWeeks) parts.push(`Week ${info.week + 1} · ${info.deload ? 'deload' : (info.label || '').replace(/^Week \d+ · /, '')}`.replace(/ · $/, ''));
  if (day?.day) parts.push(inDays === 0 ? 'planned for today' : `planned for ${day.day}`);
  let reason = parts.join(' · ');
  if (doneNames.length) reason += `${reason ? ' — ' : ''}${list(doneNames)} done`;
  else if (info.index > 0 && !doneNames.length) reason += `${reason ? ' — ' : ''}new week`;
  if (missedDays.length) reason += `; last week skipped ${list(missedDays.map(m => m.name))}`;
  return {
    program, day, index,
    weekIndex: info.index, week: info.week, weekLabel: info.label, deload: info.deload, cycle: info.cycle, finished: info.finished, hasWeeks,
    today: inDays === 0, plannedFor: day?.day || '', inDays,
    doneDayIds: info.daysDone.filter(id => !info.daysSkipped.includes(id)), skippedDayIds: info.daysSkipped, missedDays,
    active: live, trainedToday, reason,
  };
}

// Explicitly skip a program day: it counts as done for this rotation, so the
// plan moves on without pretending the lift was trained. → updated program.
export function skipProgramDay(program, dayId, now = Date.now()) {
  if (!program || !dayId) return program;
  const skips = (program.skips || []).filter(k => k && k.at >= (program.startedAt || 0)).slice(-50);
  return { ...program, skips: [...skips, { dayId, at: now }] };
}

// Pin the plan to a week (0-based absolute index) — "I'm actually in week 3".
export function withCurrentWeek(program, sessions, index, now = Date.now()) {
  const cur = programWeekInfo({ program, sessions, now }).index;
  return { ...program, weekOffset: (program.weekOffset || 0) + (Math.max(0, index | 0) - cur) };
}
// Start the plan over from week 1 (new block / new meso).
export const restartPlan = (program, now = Date.now()) => ({ ...program, startedAt: now, weekOffset: 0 });

// Snap every target weight and training max of a program to the user's
// loadable grid (2.5 kg / 1.25 kg for small lifts, 5 lb / 2.5 lb). Used for
// built-in starters and when switching units.
export function roundProgramLoads(program, units = 'lb') {
  if (!program) return program;
  const tms = program.tms ? Object.fromEntries(Object.entries(program.tms).map(([k, v]) => [k, v > 0 ? roundTo(v, units) : v])) : program.tms;
  return {
    ...program,
    ...(tms ? { tms } : {}),
    days: (program.days || []).map(d => ({
      ...d,
      exercises: (d.exercises || []).map(pe => (pe.targetWeight > 0 ? { ...pe, targetWeight: roundTo(pe.targetWeight, units, isSmall(pe) || pe.targetWeight < 40) } : pe)),
    })),
  };
}

// ——— Training maxes ——————————————————————————————————————————————————————

export const tmKeyOf = (pe) => pe?.schemeCfg?.tmKey || pe?.exerciseId || pe?.id;
export function tmFor(program, pe) {
  const k = tmKeyOf(pe);
  const v = program?.tms?.[k];
  return v > 0 ? v : (pe?.tm > 0 ? pe.tm : 0);
}
// Training max from a recent best set: 90 % of its estimated 1RM.
export function trainingMaxFrom(w, reps, units = 'lb') {
  const est = e1rm(w, reps);
  return est > 0 ? roundTo(est * 0.9, units) : 0;
}
// Training max from the lifter's log: 90 % of the best e1RM in the last
// `weeks` weeks (RPE-aware, sets of 1–12 reps). → { tm, w, reps, rpe, at, e1 } | null
export function tmFromHistory(sessions, exerciseId, { units = 'lb', now = Date.now(), weeks = 12 } = {}) {
  if (!Array.isArray(sessions) || !exerciseId) return null;
  const since = now - weeks * 7 * 86400000;
  let best = null;
  for (const s of sessions) {
    if (!s || !s.completedAt || (s.startedAt || s.completedAt) < since) continue;
    for (const st of s.sets || []) {
      if (st.exerciseId !== exerciseId || !isWorkType(st.type) || !(st.w > 0) || !(st.reps > 0) || st.reps > 12) continue;
      const e = e1rmRPE(st.w, st.reps, st.rpe);
      if (!best || e > best.e1) best = { e1: e, w: st.w, reps: st.reps, rpe: st.rpe ?? null, at: s.startedAt || s.completedAt };
    }
  }
  return best ? { ...best, tm: roundTo(best.e1 * 0.9, units) } : null;
}

// Are per-muscle check-ins (pump / soreness / joints) worth asking in this
// workout? They only steer 'rp_sets' (mesocycle) slots, and never on a deload.
//   → { mode: 'meso' | 'joint', muscles: Set<string> (meso slot muscles), reason }
// 'joint' = a single optional joint question at the finish (it flags exercises
// to swap next time; it changes no sets).
export function checkinMode({ program = null, session = null, slots = null } = {}) {
  const list = Array.isArray(slots) ? slots : (session?.exercises || []);
  const deload = !!(session && (session.planDeload || session.deload || session.lighter))
    || (program && session && session.weekIndex != null && weekInfo(program, session.weekIndex).deload);
  const meso = list.filter(sl => sl && (sl.scheme === 'rp_sets' || (program && (program.days || []).some(d => (d.exercises || []).some(pe => pe.id === sl.id && schemeOf(pe) === 'rp_sets')))));
  if (!meso.length) return { mode: 'joint', muscles: new Set(), reason: 'No mesocycle exercises today' };
  if (deload) return { mode: 'joint', muscles: new Set(), reason: 'Deload week: sets are fixed' };
  const muscles = new Set();
  for (const sl of meso) {
    if (sl.group) muscles.add(sl.group);
    for (const m of sl.primary || []) muscles.add(m);
  }
  return { mode: 'meso', muscles, reason: 'Sets next week follow your answers' };
}

// Starting a program day that is already done in this rotation begins the
// next week (programWeekInfo closes the rotation). → null when nothing to
// confirm, else { day, nextDay (the first day not done), weekNow, weekNext,
// missing: [day names left undone] }.
export function repeatDayInfo(state, programId, dayId, now = Date.now()) {
  const program = (state?.programs || []).find(p => p.id === programId);
  if (!program || !dayId) return null;
  const info = programWeekInfo({ program, sessions: state.sessions || [], now });
  const done = new Set(info.daysDone);
  if (!done.has(dayId) || done.size >= (program.days || []).length) return null;
  const day = program.days.find(d => d.id === dayId);
  const left = program.days.filter(d => !done.has(d.id));
  if (!day || !left.length) return null;
  return { day, nextDay: left[0], weekNow: info.week + 1, weekNext: weekInfo(program, info.index + 1).week + 1, hasWeeks: cycleOf(program).weeks.length > 1, missing: left.map(d => d.name) };
}

// Lifts that need a TM before the program can prescribe loads.
export function tmLifts(program) {
  const out = new Map();
  for (const d of program?.days || []) for (const pe of d.exercises || []) {
    if (schemeOf(pe) !== 'percent') continue;
    const key = tmKeyOf(pe);
    if (!out.has(key)) out.set(key, { key, name: pe.schemeCfg?.tmName || (key === pe.exerciseId ? pe.name : null), tm: tmFor(program, pe), peIds: [] });
    const row = out.get(key);
    row.peIds.push(pe.id);
    if (!row.name && key === pe.exerciseId) row.name = pe.name;
  }
  return [...out.values()].map(r => ({ ...r, name: r.name || 'Training max' }));
}
export const missingTMs = (program) => tmLifts(program).filter(r => !(r.tm > 0));
export function withTrainingMaxes(program, map) {
  const tms = { ...(program.tms || {}) };
  for (const [k, v] of Object.entries(map || {})) if (v > 0) tms[k] = v;
  return { ...program, tms };
}

// ——— Prescription ——————————————————————————————————————————————————————————

function waveOf(pe) {
  const cfg = pe.schemeCfg || {};
  if (Array.isArray(cfg.weeks) && cfg.weeks.length) return { weeks: cfg.weeks, tmRule: cfg.tmRule || 'none', bumpWeek: cfg.bumpWeek, testWeek: cfg.testWeek };
  return WAVES[cfg.wave] || WAVES.w531;
}
function expandSpecs(specs) {
  const out = [];
  for (const sp of specs) for (let i = 0; i < (sp.sets || 1); i++) out.push({ ...sp, sets: undefined });
  return out;
}

const GZ = {
  1: [{ sets: 5, reps: 3, label: '5×3+' }, { sets: 6, reps: 2, label: '6×2+' }, { sets: 10, reps: 1, label: '10×1+' }],
  2: [{ sets: 3, reps: 10, label: '3×10' }, { sets: 3, reps: 8, label: '3×8' }, { sets: 3, reps: 6, label: '3×6' }],
  3: [{ sets: 3, reps: 15, label: '3×15+' }],
};
// RIR targets across a meso's accumulation weeks, linear from `start` to `end`
// (4 weeks, 3 → 0: [3, 2, 1, 0]).
export function rirRamp(weeks = 4, start = 3, end = 0) {
  const n = Math.max(1, weeks | 0);
  if (n === 1) return [end];
  return Array.from({ length: n }, (_, i) => Math.round(start - (start - end) * i / (n - 1)));
}

const gzTier = (pe) => [1, 2, 3].includes(pe.schemeCfg?.tier) ? pe.schemeCfg.tier : 1;
const gzStage = (pe) => Math.min(GZ[gzTier(pe)].length - 1, Math.max(0, pe.schemeState?.stage || 0));

// Most recent e1RM for an exercise from completed sessions (RPE-aware).
function e1rmFromHistory(history, exerciseId) {
  if (!Array.isArray(history)) return 0;
  const done = history.filter(s => s && s.completedAt && (s.sets || []).some(st => st.exerciseId === exerciseId))
    .sort((a, b) => b.completedAt - a.completedAt);
  for (const s of done) {
    let best = 0;
    for (const st of s.sets) {
      if (st.exerciseId !== exerciseId || !isWorkType(st.type) || !(st.w > 0) || !(st.reps > 0)) continue;
      best = Math.max(best, e1rmRPE(st.w, st.reps, st.rpe));
    }
    if (best > 0) return best;
  }
  return 0;
}

// ——— Meso load and rep targets (rp_sets) ——————————————————————————————————
// RIR falls 3 → 0 across the accumulation weeks, so at the same weight the
// reps must rise (or, once the range is topped, the weight). Each week's target
// comes from last week's logged sets of the same slot: its e1RM at the RIR it
// was done at (logged RPE, else the prescribed RIR) → the reps that weight
// allows at this week's RIR, never fewer than last week's reps plus the RIR
// drop; past the top of the range the weight steps up. With no logged week in
// this meso the target comes from the latest e1RM of the lift. Weeks further
// ahead are projected as if every target is met.

// Reps a lifter with this e1RM can do with `w` leaving `rir` in reserve.
export function repsAt(e1, w, rir = 0) {
  if (!(e1 > 0) || !(w > 0)) return 0;
  const rpe = Math.max(6, Math.min(10, 10 - rir));
  let best = 0;
  for (let reps = 1; reps <= 30; reps++) {
    if (e1 * pctOf1RM(reps, rpe) >= w - 0.01) best = reps; else break;
  }
  return best;
}

function mesoAnchor({ program, pe, wk, history }) {
  if (!Array.isArray(history) || !pe) return null;
  const done = history.filter(s => s && s.completedAt && (!program?.id || s.programId === program.id)
    && (s.sets || []).some(st => st.peId === pe.id && isWorkType(st.type) && st.reps > 0))
    .sort((a, b) => (b.startedAt || b.completedAt) - (a.startedAt || a.completedAt));
  for (const s of done) {
    const wi = Number.isInteger(s.weekIndex) ? s.weekIndex : null;
    if (wi != null && wi >= wk.index) continue; // this week or later: not "last week"
    const work = (s.sets || []).filter(st => st.peId === pe.id && isWorkType(st.type) && st.reps > 0).sort((a, b) => a.setIdx - b.setIdx);
    const slot = (s.exercises || []).find(x => x && x.id === pe.id);
    const plannedRir = (slot?.rows || []).find(x => x && x.rir != null)?.rir;
    return { session: s, weekIndex: wi, info: wi != null ? weekInfo(program, wi) : null, work, plannedRir };
  }
  return null;
}

function mesoTarget({ program, pe, wk, history, rirTable, r, kind, prefs, units, small }) {
  const repMin = pe.repMin || 8;
  const repMax = Math.max(repMin, pe.repMax || repMin);
  const clamp = (x) => Math.max(repMin, Math.min(repMax, Math.round(x)));
  const rirAt = (idx) => rirTable[Math.min(weekInfo(program, idx).week, rirTable.length - 1)] ?? 0;
  const inc = incFor(defaultInc(pe), units);
  const up = (w) => loadUp(w, inc, { kind, prefs, units, small: small || w < 40 });
  const assisted = isAssisted(pe);
  const anchor = mesoAnchor({ program, pe, wk, history });
  const base = pe.targetWeight || 0;
  // One week forward from { w, reps, rir }, assuming last week's target was met.
  const step = (prev, rirNext, topped = false) => {
    if (!(prev.w > 0) || assisted) return { w: prev.w, reps: Math.min(repMax, prev.reps + Math.max(0, prev.rir - rirNext)) };
    const e1 = e1rmRPE(prev.w, prev.reps, 10 - prev.rir);
    if (topped || prev.reps >= repMax) {
      const w = up(prev.w);
      return { w, reps: clamp(repsAt(e1, w, rirNext) || repMin) };
    }
    const want = Math.max(repsAt(e1, prev.w, rirNext), prev.reps + Math.max(0, prev.rir - rirNext));
    if (want > repMax) { const w = up(prev.w); return { w, reps: clamp(repsAt(e1, w, rirNext) || repMin) }; }
    return { w: prev.w, reps: clamp(want) };
  };
  const sameMeso = !!anchor && anchor.work.length > 0 && !(anchor.info ? anchor.info.deload : anchor.session.deload)
    && (anchor.info ? anchor.info.cycle === wk.cycle : wk.week > 0);
  // The last hard week's load (for the deload).
  const lastW = anchor && anchor.work.length ? Math.max(...anchor.work.map(s => s.w || 0)) : base;
  if (wk.deload) return { w: 0, reps: repMin, lastW };
  const rirNow = rirAt(wk.index);
  if (sameMeso) {
    const wTop = Math.max(...anchor.work.map(s => s.w || 0));
    const atTop = anchor.work.filter(s => (s.w || 0) === wTop);
    const top = atTop.reduce((a, b) => (b.reps > a.reps ? b : a), atTop[0]);
    const prevRir = top.rpe >= 6 ? Math.max(0, Math.round(10 - top.rpe)) : (anchor.plannedRir ?? (anchor.weekIndex != null ? rirAt(anchor.weekIndex) : rirNow + 1));
    const prev = { w: wTop, reps: top.reps, rir: prevRir };
    const topped = atTop.length === anchor.work.length && anchor.work.every(s => s.reps >= repMax);
    const from = anchor.weekIndex != null ? anchor.weekIndex : wk.index - 1;
    let cur = { ...prev };
    let k = from + 1;
    for (; k <= wk.index; k++) {
      const rk = rirAt(k);
      const nx = step(cur, rk, k === from + 1 && topped);
      cur = { ...nx, rir: rk };
    }
    return { w: cur.w, reps: cur.reps, prev, projected: wk.index - from > 1, lastW };
  }
  // Fresh meso (or the first time): start from the lift's recent e1RM.
  const e1 = recentE1(history, pe.exerciseId);
  let w = base > 0 ? r(base) : 0;
  if (e1 > 0 && !assisted) {
    const firstRir = rirTable[0] ?? 3;
    if (!(w > 0) || repsAt(e1, w, firstRir) < repMin) w = loadable(e1 * pctOf1RM(repMin, 10 - firstRir), { kind, prefs, units, small: small || w < 40, dir: -1 });
    let reps = repsAt(e1, w, rirNow);
    if (reps > repMax) { w = up(w); reps = repsAt(e1, w, rirNow); }
    return { w, reps: clamp(reps || repMin), prev: null, lastW };
  }
  return { w, reps: repMin, prev: null, lastW };
}

// Best e1RM of a lift over its last `n` hard sessions (deload and "lighter"
// sessions skipped; a set without a logged RPE uses the effort it was
// prescribed at, else counts as all-out).
function recentE1(history, exerciseId, n = 3) {
  if (!Array.isArray(history) || !exerciseId) return 0;
  const done = history.filter(s => s && s.completedAt && !s.deload && !s.planDeload && !s.lighter
    && (s.sets || []).some(st => st.exerciseId === exerciseId && isWorkType(st.type) && st.w > 0 && st.reps > 0))
    .sort((a, b) => (b.startedAt || b.completedAt) - (a.startedAt || a.completedAt)).slice(0, n);
  let best = 0;
  for (const s of done) {
    for (const st of s.sets) {
      if (st.exerciseId !== exerciseId || !isWorkType(st.type) || !(st.w > 0) || !(st.reps > 0)) continue;
      let rpe = st.rpe;
      if (!(rpe >= 6)) {
        const row = ((s.exercises || []).find(x => x && x.id === st.peId)?.rows || [])[st.setIdx];
        rpe = row && row.rir != null ? 10 - row.rir : row && row.rpe >= 6 ? row.rpe : null;
      }
      best = Math.max(best, e1rmRPE(st.w, st.reps, rpe));
    }
  }
  return best;
}

function rpeForWeek(cfg, wk) {
  const r = cfg.rpe;
  if (Array.isArray(r)) return r[Math.min(wk.week, r.length - 1)] ?? 8;
  return typeof r === 'number' ? r : 8;
}

// prescribe({ program, pe, weekIndex, history, units, sessionSets })
//   → { scheme, rows: [{ type, reps, repsMax?, amrap?, target?, pct?, rpe?, rir?, w, top?, backoff? }],
//       note, week: weekInfo, needsTM?, tm? }
// `w` is lb, already rounded to a loadable weight; w === 0 means "find your weight".
// `sessionSets` (optional) = this slot's sets logged so far today; rpe_topset uses
// them to set back-off loads from today's top set.
// `prefs` (state.prefs) and `exercises` (state.exercises) are optional: with
// them every load lands on the lifter's own bar, plates and dumbbells.
export function prescribe({ program, pe, weekIndex = 0, history = null, units = 'lb', sessionSets = null, prefs = null, exercises = null } = {}) {
  const scheme = schemeOf(pe);
  const wk = weekInfo(program, weekIndex);
  const cfg = pe.schemeCfg || {};
  const small = isSmall(pe);
  const kind = equipmentKind(pe, exercises);
  const r = (lb) => loadable(lb, { kind, prefs, units, small: small || lb < 40 });
  const base = pe.targetWeight || 0;

  if (scheme === 'linear') {
    const n = Math.max(1, cfg.sets || pe.sets || 3);
    const reps = pe.repMin || 5;
    const w = base > 0 ? r(base) : 0;
    const fails = pe.schemeState?.fails || 0;
    const rows = Array.from({ length: n }, () => ({ type: 'normal', reps, w }));
    // cfg.amrapLast: the last set is a "+" set (at least `reps`, as many as you can).
    if (cfg.amrapLast) rows[n - 1] = { ...rows[n - 1], amrap: true, target: reps };
    const plus = cfg.amrapLast ? ` Last set as many as you can${cfg.doubleAt ? ` — ${cfg.doubleAt}+ doubles the jump` : ''}.` : '';
    return { scheme, rows, week: wk, note: fails ? `Missed last time (${fails} of ${cfg.failLimit || 3} before a 10 % reset). Same weight, make every rep.` : `${n}×${reps}${cfg.amrapLast ? '+' : ''}. Make every rep and the weight goes up next time.${plus}` };
  }

  if (scheme === 'percent') {
    const wave = waveOf(pe);
    const specs = wave.weeks[wk.week % wave.weeks.length] || [];
    let tm = tmFor(program, pe);
    // No training max yet: estimate one from the lifter's log (90 % of the
    // latest e1RM) so the first workout still has loads. afterSession() then
    // sets the real one from the sets they log.
    let tmEstimated = false;
    if (!(tm > 0)) {
      const est = recentE1(history, tmKeyOf(pe));
      if (est > 0) { tm = roundTo(est * 0.9, units); tmEstimated = true; }
    }
    const rows = expandSpecs(specs).map(sp => {
      const row = { type: 'normal', reps: sp.reps, pct: sp.pct, w: tm > 0 ? r(tm * sp.pct / 100) : 0 };
      if (sp.amrap) { row.amrap = true; row.target = sp.target || sp.reps; }
      return row;
    });
    const plus = rows.find(x => x.amrap);
    const barOnly = kind === 'barbell' && tm > 0 && rows.some(x => x.w > 0 && x.w <= loadable(1, { kind, prefs, units }) + 1e-6)
      && rows.some(x => tm * x.pct / 100 < x.w - 1e-6);
    let note = !(tm > 0)
      ? 'Set a training max to get loads for this lift.'
      : plus ? (plus.target > plus.reps ? `Last set: rep out, aim for ${plus.target}+ (at least ${plus.reps}). Stop 1 rep short of failure.` : `"+" set: as many good reps as you can, at least ${plus.reps}.`)
      : wk.deload ? 'Deload: easy, fast reps.' : `Training max ${fmtW(tm, units)} ${U(units)}.`;
    if (barOnly) note += ' Light sets are the bar only.';
    if (tmEstimated) note += ` Training max estimated at ${fmtW(tm, units)} ${U(units)} from your last session — your sets today set the real one.`;
    return { scheme, rows, week: wk, note, tm, needsTM: !(tm > 0), ...(tmEstimated ? { tmEstimated: true } : {}) };
  }

  if (scheme === 'rpe_topset') {
    const topReps = cfg.topReps || pe.repMin || 3;
    const rpe = wk.deload ? Math.min(6, rpeForWeek(cfg, wk)) : rpeForWeek(cfg, wk);
    const nBack = wk.deload ? Math.max(1, Math.ceil((cfg.backoffs ?? 3) / 2)) : (cfg.backoffs ?? 3);
    const backReps = cfg.backoffReps || topReps;
    const drop = cfg.drop ?? 10;
    const est = pe.schemeState?.e1rm > 0 ? pe.schemeState.e1rm : e1rmFromHistory(history, pe.exerciseId);
    let topW = est > 0 ? r(est * pctOf1RM(topReps, rpe)) : (base > 0 ? r(base) : 0);
    // Today's top set (if logged) re-derives the back-offs.
    let today = 0;
    const top = (sessionSets || []).filter(st => isWorkType(st.type) && st.w > 0 && st.reps > 0).sort((a, b) => a.setIdx - b.setIdx)[0];
    if (top) today = e1rmRPE(top.w, top.reps, top.rpe ?? rpe);
    const backBase = today > 0 ? today * pctOf1RM(topReps, rpe) : topW;
    const backW = backBase > 0 ? r(backBase * (1 - drop / 100)) : 0;
    const rows = [{ type: 'normal', reps: topReps, rpe, w: topW, top: true }]
      .concat(Array.from({ length: nBack }, () => ({ type: 'normal', reps: backReps, w: backW, pct: -drop, backoff: true })));
    const note = topW > 0
      ? `Top set ${topReps} @ RPE ${rpe}, then ${nBack}×${backReps} at −${drop} %${today ? ' from today\'s top set' : ''}. Log RPE on the top set.`
      : `Work up to a ${topReps}-rep set at RPE ${rpe} (about ${10 - rpe} rep${10 - rpe === 1 ? '' : 's'} left), then ${nBack}×${backReps} at −${drop}%.`;
    return { scheme, rows, week: wk, note, e1rm: today || est || 0 };
  }

  if (scheme === 'gzcl') {
    const tier = gzTier(pe);
    const st = GZ[tier][gzStage(pe)];
    const w = base > 0 ? r(base) : 0;
    const rows = Array.from({ length: st.sets }, (_, i) => {
      const row = { type: 'normal', reps: st.reps, w };
      if (tier !== 2 && i === st.sets - 1) { row.amrap = true; row.target = tier === 3 ? 25 : st.reps; }
      return row;
    });
    const note = tier === 1 ? `T1 ${st.label}: last set as many as you can. Miss a rep and the next session moves to the next stage.`
      : tier === 2 ? `T2 ${st.label}: make every rep to add weight; a miss moves to the next stage.`
      : 'T3: last set as many as you can — 25+ and the weight goes up.';
    return { scheme, rows, week: wk, note, stage: st.label, tier };
  }

  if (scheme === 'rp_sets') {
    const mev = Math.max(1, cfg.mev || pe.sets || 2);
    const mrv = Math.max(mev, cfg.mrv || mev + 3);
    const accWeeks = cycleOf(program).weeks.filter(w => !w.deload).length || 4;
    const rirTable = Array.isArray(cfg.rir) && cfg.rir.length ? cfg.rir : rirRamp(accWeeks, cfg.rirStart ?? 3, cfg.rirEnd ?? 0);
    const tgt = mesoTarget({ program, pe, wk, history, rirTable, r, kind, prefs, units, small });
    if (wk.deload) {
      // Half the sets, 15 % under the last hard week's load, 4+ reps in reserve.
      const n = Math.max(1, Math.ceil(mev / 2));
      const from = tgt.lastW > 0 ? tgt.lastW : base;
      const w = from > 0 ? loadable(from * 0.85, { kind, prefs, units, small: small || from < 40, dir: -1 }) : 0;
      const reps = pe.repMin || 8;
      return {
        scheme, rows: Array.from({ length: n }, () => ({ type: 'normal', reps, repsMax: reps, rir: 4, rpe: 6, w })), week: wk, sets: n,
        note: `Deload: ${n} set${n === 1 ? '' : 's'} of ${reps}${w > 0 ? ` at ${fmtW(w, units)} ${U(units)}` : ''} — half the sets, 15 % lighter, stop 4+ reps short of failure.`,
      };
    }
    const st = pe.schemeState || {};
    const adj = st.adjCycle === wk.cycle ? (st.adj || 0) : 0;
    const n = Math.max(mev, Math.min(mrv, mev + wk.week + adj));
    const rir = rirTable[Math.min(wk.week, rirTable.length - 1)] ?? 0;
    const repMax = pe.repMax || pe.repMin || 12;
    const rows = Array.from({ length: n }, () => ({ type: 'normal', reps: tgt.reps || pe.repMin || 8, repsMax: repMax, rir, rpe: 10 - rir, w: tgt.w }));
    if (tgt.prev) rows.forEach(row => { row.prev = tgt.prev; });
    // Feedback-driven weeks say why the set count is what it is.
    const fbWhy = st.fb && st.fb.week === wk.index - 1 && st.adjCycle === wk.cycle ? ` ${st.fb.reason}.` : '';
    const tText = tgt.w > 0 ? ` Target ${fmtW(tgt.w, units)} × ${rows[0].reps}${tgt.prev ? ` (last week ${fmtW(tgt.prev.w, units)} × ${tgt.prev.reps} at ${tgt.prev.rir} RIR)` : ''}.`
      : tgt.prev ? ` Target ${rows[0].reps} reps (last week ${tgt.prev.reps}).` : '';
    const note = `${n} sets at ${rir} rep${rir === 1 ? '' : 's'} in reserve${rir === 0 ? ' (to failure on the last set)' : ''}.${tText}${fbWhy || (adj < 0 ? ' Sets held back — last week\'s reps dropped.' : '')}${st.joint === 'bad' ? ' Joint pain flagged last time: swap it if it still hurts.' : ''}`;
    return { scheme, rows, week: wk, note, sets: n, rir, flagged: st.joint === 'bad', target: { w: tgt.w, reps: rows[0].reps, prev: tgt.prev || null, projected: !!tgt.projected } };
  }

  // double (default)
  const n = Math.max(1, pe.sets || 3);
  const w = base > 0 ? r(base) : 0;
  return {
    scheme: 'double', week: wk,
    rows: Array.from({ length: n }, () => ({ type: 'normal', reps: pe.repMin || 8, repsMax: pe.repMax || pe.repMin || 8, w })),
    note: `Hit ${pe.repMax || pe.repMin} on every set and the weight goes up.`,
  };
}

// Live back-off adjustment for rpe_topset during a workout: returns the lb
// load for row `rowIdx` (working-row index) given the slot's logged sets, or null.
export function liveLoad({ program, pe, weekIndex = 0, units = 'lb', sessionSets = [], workIdx = 0 } = {}) {
  if (schemeOf(pe) !== 'rpe_topset') return null;
  const rx = prescribe({ program, pe, weekIndex, units, sessionSets });
  const row = rx.rows[workIdx];
  return row ? row.w : null;
}

// Live suggestion for a slot whose rows were baked from prescribe() at session
// start (rows carry w/reps/amrap/pct/rpe/top/backoff). `workIdx` counts non-warm-up
// rows (same as session_model.workIndexOf). Back-off rows are re-derived from
// today's logged top set. → { w, reps, tier: 'plan', note? } or null (fall back).
export function planSuggestion({ slot, workIdx = 0, loggedSets = [], units = 'lb' } = {}) {
  if (!slot || !slot.scheme || slot.scheme === 'double') return null;
  const working = (slot.rows || []).filter(r => r.type !== 'warmup' && r.type !== 'mini');
  const row = working[workIdx];
  if (!row || row.type === 'drop' || row.w == null) return null;
  let w = row.w;
  if (row.backoff) {
    const topRow = working.find(r => r.top);
    const top = (loggedSets || []).filter(st => isWorkType(st.type) && st.w > 0 && st.reps > 0).sort((a, b) => a.setIdx - b.setIdx)[0];
    if (topRow && top) {
      const rpe = topRow.rpe ?? 8;
      const e1 = e1rmRPE(top.w, top.reps, top.rpe ?? rpe);
      w = roundTo(e1 * pctOf1RM(topRow.reps, rpe) * (1 + (row.pct || -10) / 100), units, w < 40);
    }
  }
  if (!(w > 0)) return null;
  const note = row.amrap
    ? (row.target > row.reps ? `Rep out: aim for ${row.target}, at least ${row.reps}. Stop a rep short of failure.` : `+ set: as many good reps as you can, at least ${row.reps}.`)
    : row.top ? `Top set: ${row.reps} @ RPE ${row.rpe}. Log the RPE — back-offs follow it.`
    : row.backoff ? `Back-off ${-(row.pct || -10)}% under today's top set.`
    : row.rir != null ? `${row.rir} rep${row.rir === 1 ? '' : 's'} in reserve.${row.prev && row.prev.w > 0 ? ` Last week ${fmtW(row.prev.w, units)} × ${row.prev.reps} at ${row.prev.rir} RIR.` : ''}` : undefined;
  return { w, reps: row.reps, tier: 'plan', note };
}

// ——— After the session ——————————————————————————————————————————————————————

// Working sets of a slot in logged order (warm-ups and drop sets excluded).
function workingSets(session, peId) {
  // A logged 0-rep set counts (a missed "+" set or a failed rep is information).
  return (session.sets || []).filter(st => st.peId === peId && isWorkType(st.type) && st.reps >= 0 && st.reps != null && (st.reps > 0 || st.w > 0))
    .sort((a, b) => a.setIdx - b.setIdx);
}

const rtfDelta = (d) => d <= -2 ? -0.05 : d === -1 ? -0.02 : d === 0 ? 0 : Math.min(0.03, d * 0.005 + (d >= 5 ? 0.005 : 0));
function pyramidBump(reps, small) {
  if (reps <= 1) return 0;
  if (reps <= 3) return 5;
  if (reps <= 5) return small ? 5 : 10;
  return small ? 10 : 15;
}

// afterSession({ program, session, slots, units, weekIndex? })
//   → { program, changes: [{ peId, exerciseId, name, kind, text, from?, to? }],
//       progressed: [{ peId, name, from, to, inc }] }   // weight increases, summary-compatible
// Deload weeks (cycle) hold every target. A session flagged deload/lighter (the
// manual toggles) changes nothing.
export function afterSession({ program, session, slots = [], units = 'lb', weekIndex, prefs = null, exercises = null } = {}) {
  const out = { program, changes: [], progressed: [] };
  if (!program || !session || session.deload || session.lighter) return out;
  const wk = weekInfo(program, weekIndex ?? session.weekIndex ?? 0);
  let prog = { ...program, tms: { ...(program.tms || {}) }, days: program.days.map(d => ({ ...d, exercises: d.exercises.slice() })) };
  const linked = {}; // exerciseId → new linear target/state (applied to every linear slot of that lift)
  const linkedE1 = {}; // exerciseId → today's e1RM (shared by every top-set slot of that lift)
  const say = (pe, kind, text, from, to, extra) => out.changes.push({ peId: pe.id, exerciseId: pe.exerciseId, name: pe.name, kind, text, from, to, ...extra });
  const U_ = U(units);
  const meso = []; // rp_sets slots with session feedback for their muscle
  const seeded = new Set(); // TM keys first set from today's sets

  const day = prog.days.find(d => d.id === session.dayId);
  if (!day) return out;
  day.exercises = day.exercises.map(pe => {
    const slot = slots.find(s => s.id === pe.id && s.fromProgram !== false && s.exerciseId === pe.exerciseId);
    if (!slot) return pe;
    const work = workingSets(session, pe.id);
    if (!work.length) return pe;
    const scheme = schemeOf(pe);
    const cfg = pe.schemeCfg || {};
    const st = { ...(pe.schemeState || {}) };
    const small = isSmall(pe);
    const inc = incFor(defaultInc(pe), units);
    // Loads land on the lifter's own bar, plates and dumbbells.
    const kind = equipmentKind(pe, exercises);
    const L = (lb, dir = 0) => loadable(lb, { kind, prefs, units, small: small || lb < 40, dir });
    const LU = (from, by) => loadUp(from, by, { kind, prefs, units, small: small || from < 40 });
    const wMax = Math.max(...work.map(s => s.w || 0));
    const plannedWorking = (slot.rows || []).filter(r => isWorkType(r.type)).length || pe.sets || 1;
    const assisted = isAssisted(pe);

    if (scheme === 'linear' && assisted) {
      // Assisted: make every rep and the assistance drops; misses hold it, then add 10 %.
      const reps = pe.repMin || 5;
      const target = pe.targetWeight || 0;
      const used = work.length ? Math.min(...work.map(s => s.w || 0)) : target;
      const made = work.length >= plannedWorking && work.every(s => s.reps >= reps && (s.w || 0) <= Math.max(used, target) + 0.01);
      const limit = cfg.failLimit || 3;
      let to = target, fails = st.fails || 0;
      if (made) {
        to = used - inc > 0.01 ? roundTo(used - inc, units, small) : 0; fails = 0;
        say(pe, 'weight', to > 0 ? `${pe.name}: all ${plannedWorking}×${reps} made — assistance down to ${fmtW(to, units)} ${U_}.` : `${pe.name}: all ${plannedWorking}×${reps} made — try it without assistance next time.`, used, to);
        out.progressed.push({ peId: pe.id, name: pe.name, from: used, to, inc: round2(to - used) });
      } else if (fails + 1 >= limit) {
        to = roundTo(Math.max(used, target) * 1.1 + 0.01, units, small); fails = 0;
        say(pe, 'deload', `${pe.name}: missed ${limit} sessions in a row — assistance up 10 % to ${fmtW(to, units)} ${U_}.`, target, to);
      } else {
        fails += 1; to = target || used;
        say(pe, 'hold', `${pe.name}: missed reps — same assistance (${fails} of ${limit}).`, to, to);
      }
      return { ...pe, targetWeight: to, schemeState: { ...st, fails } };
    }

    if (scheme === 'linear') {
      const reps = pe.repMin || 5;
      const target = pe.targetWeight || 0;
      const used = wMax || target;
      const made = work.length >= plannedWorking && work.every(s => s.reps >= reps && (s.w || 0) >= Math.min(used, target || used) - 0.01);
      const limit = cfg.failLimit || 3;
      let to = target, fails = st.fails || 0;
      if (made) {
        const last = work[work.length - 1];
        const twice = cfg.amrapLast && cfg.doubleAt > 0 && last && last.reps >= cfg.doubleAt;
        to = LU(used, inc * (twice ? 2 : 1)); fails = 0;
        say(pe, 'weight', twice
          ? `${pe.name}: ${last.reps} rep${last.reps === 1 ? '' : 's'} on the + set — double jump to ${fmtW(to, units)} ${U_} next time.`
          : `${pe.name}: all ${plannedWorking}×${reps} made — ${fmtW(to, units)} ${U_} next time.`, used, to);
        out.progressed.push({ peId: pe.id, name: pe.name, from: used, to, inc: round2(to - used) });
      } else if (fails + 1 >= limit) {
        to = L(Math.max(used, target) * 0.9); fails = 0;
        say(pe, 'deload', `${pe.name}: missed ${limit} sessions in a row — reset 10 % to ${fmtW(to, units)} ${U_} and build back.`, target, to);
      } else {
        fails += 1; to = target || used;
        say(pe, 'hold', `${pe.name}: missed reps — repeat ${fmtW(to, units)} ${U_} (${fails} of ${limit}).`, to, to);
      }
      linked[pe.exerciseId] = { targetWeight: to, fails };
      return { ...pe, targetWeight: to, schemeState: { ...st, fails } };
    }

    if (scheme === 'percent') {
      const key = tmKeyOf(pe);
      // No training max yet: today's sets set it (90 % of the best e1RM), so
      // the next workout has loads. Only the lift itself seeds its TM.
      if (!(prog.tms[key] > 0) && !(pe.tm > 0) && !wk.deload && key === pe.exerciseId && !assisted) {
        const best = work.filter(s => s.w > 0 && s.reps > 0 && s.reps <= 12)
          .reduce((b, s) => { const e = e1rmRPE(s.w, s.reps, s.rpe); return e > b.e ? { e, s } : b; }, { e: 0, s: null });
        if (best.e > 0) {
          const to = roundTo(best.e * 0.9, units);
          prog.tms[key] = to;
          seeded.add(key);
          say(pe, 'tm', `${pe.name}: training max set to ${fmtW(to, units)} ${U_} from today's ${fmtW(best.s.w, units)} × ${best.s.reps} (90 % of the estimated max).`, 0, to);
        }
        return pe;
      }
      if (seeded.has(key)) return pe;
      if (wk.deload || cfg.bumps === false) return pe;
      const wave = waveOf(pe);
      const rule = cfg.tmRule || wave.tmRule || 'none';
      const tm = prog.tms[key] || pe.tm || 0;
      if (!(tm > 0) || rule === 'none') return pe;
      const rx = prescribe({ program: prog, pe, weekIndex: wk.index, units });
      const pIdx = rx.rows.findIndex(r => r.amrap);
      const plusRow = pIdx >= 0 ? rx.rows[pIdx] : null;
      const plusSet = pIdx >= 0 ? (work[pIdx] || null) : null;
      let to = tm, why = '';
      const tInc = incFor(cfg.inc ?? (small ? 5 : 10), units);
      if (rule === 'cycle') {
        if (wk.week !== (wave.bumpWeek ?? wave.weeks.length - 2)) return pe;
        if (plusSet && plusSet.reps >= plusRow.reps) { to = tm + tInc; why = `${plusSet.reps} rep${plusSet.reps === 1 ? '' : 's'} on the + set — training max up`; }
        else if (plusSet) { to = roundTo(tm * 0.9, units); why = `+ set short (${plusSet.reps} of ${plusRow.reps}) — training max down 10 %`; }
        else return pe;
      } else if (rule === 'amrap') {
        if (!plusSet) return pe;
        const add = incFor(pyramidBump(plusSet.reps, small), units);
        if (!add) { say(pe, 'hold', `${pe.name}: ${plusSet.reps} rep on the 1+ set — training max stays ${fmtW(tm, units)} ${U_}.`, tm, tm); return pe; }
        to = tm + add; why = `${plusSet.reps} rep${plusSet.reps === 1 ? '' : 's'} on the + set`;
      } else if (rule === 'rtf') {
        if (!plusSet) return pe;
        const d = plusSet.reps - (plusRow.target || plusRow.reps);
        const pct = rtfDelta(d);
        if (pct === 0) { say(pe, 'hold', `${pe.name}: hit the rep-out target exactly — training max stays.`, tm, tm); return pe; }
        to = roundTo(tm * (1 + pct), units);
        if (to === tm) to = pct > 0 ? tm + plateStep(units, true) : tm - plateStep(units, true);
        why = `${plusSet.reps} rep${plusSet.reps === 1 ? '' : 's'} vs target ${plusRow.target || plusRow.reps} (${pct > 0 ? '+' : ''}${Math.round(pct * 1000) / 10} %)`;
      } else if (rule === 'success') {
        const made = work.length >= rx.rows.length && rx.rows.every((row, i) => (work[i]?.reps || 0) >= row.reps);
        const fails = st.fails || 0;
        if (made) { to = tm + incFor(cfg.inc ?? (small ? 2.5 : 5), units); why = 'every rep made'; st.fails = 0; }
        else if (fails + 1 >= (cfg.failLimit || 2)) { to = roundTo(tm * 0.9, units); why = 'missed twice — reset 10 %'; st.fails = 0; }
        else { st.fails = fails + 1; say(pe, 'hold', `${pe.name}: missed reps — same training max next week.`, tm, tm); return { ...pe, schemeState: st }; }
      } else if (rule === 'test') {
        if (wk.week !== (wave.testWeek ?? wave.weeks.length - 1)) return pe;
        const best = Math.max(0, ...work.map(s => e1rmRPE(s.w, s.reps, s.rpe)));
        const next = roundTo(best * 0.9, units);
        if (!(next > tm)) { say(pe, 'hold', `${pe.name}: test week done — training max stays ${fmtW(tm, units)} ${U_}.`, tm, tm); return pe; }
        to = next; why = `new estimated max ${fmtW(roundTo(best, units), units)} ${U_}`;
      }
      to = roundTo(to, units, true); // on the loadable grid (no float drift over many bumps)
      if (to === tm) return pe;
      prog.tms[key] = to;
      say(pe, 'tm', `${pe.name}: training max ${fmtW(tm, units)} → ${fmtW(to, units)} ${U_} (${why}).`, tm, to);
      return { ...pe, schemeState: st };
    }

    if (scheme === 'rpe_topset') {
      const rpeT = wk.deload ? 6 : rpeForWeek(cfg, wk);
      const top = work[0];
      const est = e1rmRPE(top.w, top.reps, top.rpe ?? rpeT);
      if (!(est > 0)) return pe;
      const prev = st.e1rm || 0;
      const topReps = cfg.topReps || pe.repMin || 3;
      const nextWk = weekInfo(prog, wk.index + 1);
      const nextRpe = nextWk.deload ? 6 : rpeForWeek(cfg, nextWk);
      const nextTop = L(est * pctOf1RM(topReps, nextRpe));
      say(pe, 'e1rm', `${pe.name}: e1RM ${prev ? fmtW(roundTo(prev, units), units) + ' → ' : ''}${fmtW(roundTo(est, units), units)} ${U_}. Next top set ≈ ${fmtW(nextTop, units)} × ${topReps} @ RPE ${nextRpe}.`, prev, est);
      if (nextTop > (pe.targetWeight || 0)) out.progressed.push({ peId: pe.id, name: pe.name, from: pe.targetWeight || top.w, to: nextTop, inc: round2(nextTop - (pe.targetWeight || top.w)) });
      linkedE1[pe.exerciseId] = round2(est);
      return { ...pe, targetWeight: nextTop, schemeState: { ...st, e1rm: round2(est) } };
    }

    if (scheme === 'gzcl') {
      const tier = gzTier(pe);
      const stage = gzStage(pe);
      const spec = GZ[tier][stage];
      const w = wMax || pe.targetWeight || 0;
      const made = work.length >= spec.sets && work.slice(0, spec.sets).every(s => s.reps >= spec.reps);
      if (tier === 3) {
        const last = work[work.length - 1];
        if (last.reps >= 25) {
          const to = LU(w, incFor(cfg.inc ?? 5, units));
          say(pe, 'weight', `${pe.name}: ${last.reps} on the last set — ${fmtW(to, units)} ${U_} next time.`, w, to);
          out.progressed.push({ peId: pe.id, name: pe.name, from: w, to, inc: round2(to - w) });
          return { ...pe, targetWeight: to };
        }
        return w !== pe.targetWeight ? { ...pe, targetWeight: w } : pe;
      }
      if (made) {
        const to = LU(w, inc);
        say(pe, 'weight', `${pe.name}: ${spec.label} complete — ${fmtW(to, units)} ${U_} next time.`, w, to);
        out.progressed.push({ peId: pe.id, name: pe.name, from: w, to, inc: round2(to - w) });
        return { ...pe, targetWeight: to, schemeState: { ...st, base: stage === 0 ? to : st.base } };
      }
      if (stage < GZ[tier].length - 1) {
        const next = GZ[tier][stage + 1];
        say(pe, 'stage', `${pe.name}: missed reps at ${spec.label} — next time ${next.label} at the same weight.`, w, w);
        return { ...pe, targetWeight: w, schemeState: { ...st, stage: stage + 1, base: stage === 0 ? w : st.base } };
      }
      // Failed the last stage → reset.
      let to;
      if (tier === 1) {
        const best = Math.max(0, ...work.map(s => e1rm(s.w, s.reps)));
        const fiveRM = best > 0 ? best * pctOf1RM(5, 10) : w;
        to = L(fiveRM * 0.85);
        say(pe, 'stage', `${pe.name}: missed at ${spec.label} — reset to 5×3+ at ${fmtW(to, units)} ${U_} (85 % of an estimated 5RM).`, w, to);
      } else {
        to = L((st.base || w) + 2 * inc);
        say(pe, 'stage', `${pe.name}: missed at ${spec.label} — back to 3×10 at ${fmtW(to, units)} ${U_}.`, w, to);
      }
      return { ...pe, targetWeight: to, schemeState: { ...st, stage: 0, base: to } };
    }

    if (scheme === 'rp_sets') {
      if (wk.deload) return pe;
      const repMin = pe.repMin || 8, repMax = pe.repMax || repMin;
      const short = work.filter(s => s.reps < repMin).length;
      let next = { ...pe };
      const adjNow = st.adjCycle === wk.cycle ? (st.adj || 0) : 0;
      const fb = feedbackFor(session.feedback, pe);
      if (fb) {
        // The set count is decided per muscle once every slot is read (below).
        meso.push({ pe, muscle: fb.muscle, fb: fb.value, n: plannedWorking, dropped: short >= Math.ceil(work.length / 2), top: work.every(s => s.reps >= repMax), adjNow });
      } else if (short >= Math.ceil(work.length / 2)) {
        next.schemeState = { ...st, adj: adjNow - 1, adjCycle: wk.cycle };
        say(pe, 'sets', `${pe.name}: reps dropped on ${short} of ${work.length} sets — holding the set count next week.`, null, null);
      } else if (st.adjCycle !== wk.cycle) {
        next.schemeState = { ...st, adj: 0, adjCycle: wk.cycle };
      }
      const atW = work.filter(s => (s.w || 0) === wMax);
      if (atW.length === work.length && work.every(s => s.reps >= repMax)) {
        const to = LU(wMax, inc);
        say(pe, 'weight', `${pe.name}: top of the range on every set — ${fmtW(to, units)} ${U_} next time.`, wMax, to);
        out.progressed.push({ peId: pe.id, name: pe.name, from: wMax, to, inc: round2(to - wMax) });
        next.targetWeight = to;
      } else if (wMax && wMax !== pe.targetWeight) next.targetWeight = wMax;
      return next;
    }

    // double — same rule as session_model.applyProgression (0-rep sets ignored)
    const done = work.filter(s => s.reps > 0);
    if (!done.length) return pe;
    if (assisted) {
      // Least assistance used is the working "weight"; topping the range at it lowers it.
      const aMin = Math.min(...done.map(s => s.w || 0));
      const atA = done.filter(s => (s.w || 0) === aMin);
      const top = done.length >= plannedWorking && atA.length === done.length && atA.every(s => s.reps >= (slot.repMax ?? pe.repMax));
      const stepA = units === 'kg' ? (inc >= 5 ? 2.5 / KG : 1.25 / KG) : inc;
      const toA = top ? (aMin - stepA > 0.01 ? roundTo(aMin - stepA, units, true) : 0) : aMin;
      if (toA === (pe.targetWeight || 0)) return pe;
      if (top) {
        out.progressed.push({ peId: pe.id, name: pe.name, from: aMin, to: toA, inc: round2(toA - aMin) });
        say(pe, 'weight', toA > 0 ? `${pe.name}: top of the range on every set — assistance down to ${fmtW(toA, units)} ${U_}.` : `${pe.name}: top of the range on every set — try it without assistance next time.`, aMin, toA);
      }
      return { ...pe, targetWeight: toA };
    }
    const dMax = Math.max(...done.map(s => s.w || 0));
    const atW = done.filter(s => (s.w || 0) === dMax);
    const hitTop = done.length >= plannedWorking && atW.length === done.length && atW.every(s => s.reps >= (slot.repMax ?? pe.repMax));
    const step = units === 'kg' ? (inc >= 5 ? 2.5 / KG : 1.25 / KG) : inc;
    // Snap to what can be loaded (a 61.2 kg target becomes 62.5, never 63.7).
    const to = hitTop ? (kind ? LU(dMax, step) : roundTo(roundTo(dMax, units, small || dMax < 40) + step, units, small || dMax < 40)) : dMax;
    if (to === (pe.targetWeight || 0)) return pe;
    if (hitTop) {
      out.progressed.push({ peId: pe.id, name: pe.name, from: dMax, to, inc: step });
      say(pe, 'weight', `${pe.name}: top of the range on every set — ${fmtW(to, units)} ${U_} next time.`, dMax, to);
    }
    return { ...pe, targetWeight: to };
  });

  // Mesocycle feedback loop: next week's sets per muscle from pump, soreness,
  // joint pain and rep performance, bounded by each exercise's MEV / MRV.
  if (meso.length) day.exercises = applyMesoFeedback({ prog, day, meso, wk, say });

  // Linked linear lifts: every linear slot of the same exercise moves together.
  if (Object.keys(linked).length || Object.keys(linkedE1).length) {
    prog.days = prog.days.map(d => ({
      ...d,
      exercises: d.exercises.map(pe => {
        const s = schemeOf(pe);
        if (s === 'linear' && linked[pe.exerciseId]) {
          return { ...pe, targetWeight: linked[pe.exerciseId].targetWeight, schemeState: { ...(pe.schemeState || {}), fails: linked[pe.exerciseId].fails } };
        }
        if (s === 'rpe_topset' && linkedE1[pe.exerciseId] && pe.schemeState?.e1rm !== linkedE1[pe.exerciseId]) {
          const est = linkedE1[pe.exerciseId];
          const c = pe.schemeCfg || {};
          const nw = weekInfo(prog, wk.index + 1);
          const top = loadable(est * pctOf1RM(c.topReps || pe.repMin || 3, nw.deload ? 6 : rpeForWeek(c, nw)), { kind: equipmentKind(pe, exercises), prefs, units, small: isSmall(pe) });
          return { ...pe, targetWeight: top, schemeState: { ...(pe.schemeState || {}), e1rm: est } };
        }
        return pe;
      }),
    }));
  }
  out.program = prog;
  return out;
}

// ——— Mesocycle feedback (RP-style) ————————————————————————————————————————
// session.feedback = { [muscle]: { pump: 'low'|'ok'|'great',
//                                  soreness: 'none'|'healed'|'sore',
//                                  joint: 'none'|'some'|'bad' } }
// Soreness is how the muscle felt going into this session (did it recover from
// last time); pump and joint describe this session.

const FB_OK = { pump: ['low', 'ok', 'great'], soreness: ['none', 'healed', 'sore'], joint: ['none', 'some', 'bad'] };
const normKey = (s) => String(s || '').toLowerCase().replace(/[^a-z]+/g, '');

// The feedback that applies to a program exercise: its muscle (pe.group, then
// primary muscles), or an entry keyed by the slot / exercise id. → { muscle, value } | null
export function feedbackFor(feedback, pe) {
  if (!feedback || typeof feedback !== 'object' || !pe) return null;
  const keys = Object.keys(feedback);
  const clean = (v) => {
    if (!v || typeof v !== 'object') return null;
    const out = {};
    for (const k of Object.keys(FB_OK)) if (FB_OK[k].includes(v[k])) out[k] = v[k];
    return Object.keys(out).length ? out : null;
  };
  const wanted = [pe.group, ...(pe.primary || [])].filter(Boolean).map(normKey);
  for (const w of wanted) {
    const k = keys.find(x => normKey(x) === w);
    const v = k && clean(feedback[k]);
    if (v) return { muscle: k, value: v };
  }
  for (const id of [pe.id, pe.exerciseId]) {
    const v = id && clean(feedback[id]);
    if (v) return { muscle: pe.group || pe.name, value: v };
  }
  return null;
}

// Set change for next week from one muscle's feedback and rep performance
// ('up' = top of the range on every set, 'held', 'dropped' = half the sets or
// more under the rep range). → { delta, why, flag }
//   joint pain 'bad'            → hold, flag the exercise
//   still sore                  → hold (−1 if reps also dropped): recovery-limited
//   recovered but reps dropped  → hold
//   recovered, reps held:  low pump → +2 (never sore) / +1;  good pump → +1;
//                          great pump → +1 (never sore) / hold (enough stimulus)
//   some joint discomfort       → never adds sets
export function feedbackDecision(fb = {}, perf = 'held') {
  const { pump, soreness, joint } = fb || {};
  let delta;
  if (joint === 'bad') delta = 0;
  else if (soreness === 'sore') delta = perf === 'dropped' ? -1 : 0;
  else if (perf === 'dropped') delta = 0;
  else if (pump === 'low') delta = soreness === 'none' ? 2 : 1;
  else if (pump === 'great') delta = soreness === 'none' ? 1 : 0;
  else delta = 1;
  if (joint === 'some') delta = Math.min(delta, 0);
  const words = [
    joint === 'bad' ? 'joint pain' : joint === 'some' ? 'some joint discomfort' : null,
    { low: 'low pump', ok: 'good pump', great: 'great pump' }[pump] || null,
    { none: 'never got sore', healed: 'recovered', sore: 'still sore' }[soreness] || null,
    { up: 'reps up', held: 'reps held', dropped: 'reps dropped' }[perf] || null,
  ].filter(Boolean);
  return { delta, why: words.join(', '), flag: joint === 'bad' };
}

const mesoBounds = (pe) => {
  const cfg = pe.schemeCfg || {};
  const mev = Math.max(1, cfg.mev || pe.sets || 2);
  return { mev, mrv: Math.max(mev, cfg.mrv || mev + 3) };
};

function applyMesoFeedback({ prog, day, meso, wk, say }) {
  const nextWk = weekInfo(prog, wk.index + 1);
  const carries = nextWk.cycle === wk.cycle && !nextWk.deload && !nextWk.finished;
  const byMuscle = new Map();
  for (const m of meso) {
    if (!byMuscle.has(m.muscle)) byMuscle.set(m.muscle, []);
    byMuscle.get(m.muscle).push({ ...m, ...mesoBounds(m.pe), share: 0 });
  }
  const updates = new Map(); // peId → schemeState
  for (const [muscle, list] of byMuscle) {
    const perf = list.some(x => x.dropped) ? 'dropped' : list.every(x => x.top) ? 'up' : 'held';
    const dec = feedbackDecision(list[0].fb, perf);
    let left = dec.delta;
    // Spread the change over the muscle's exercises, inside each one's MEV–MRV.
    // Adds go to the exercise with the fewest added so far (first wins a tie);
    // cuts come off the last exercise that is still above its MEV.
    while (left !== 0) {
      const up = left > 0;
      const cands = list.filter(y => (up ? y.n + y.share < y.mrv : y.n + y.share > y.mev));
      if (!cands.length) break;
      const x = up ? cands.reduce((a, b) => (b.share < a.share ? b : a)) : cands[cands.length - 1];
      x.share += up ? 1 : -1;
      left += up ? -1 : 1;
    }
    const applied = list.reduce((a, x) => a + x.share, 0);
    const total = list.reduce((a, x) => a + x.n + x.share, 0);
    const plural = (k) => `${k} set${Math.abs(k) === 1 ? '' : 's'}`;
    let text;
    if (!carries) text = `${muscle}: ${dec.why}${nextWk.deload ? ' — deload next week' : ' — the next meso starts back at MEV'}.`;
    else if (applied > 0) text = `${muscle}: +${plural(applied)} — ${dec.why}.`;
    else if (applied < 0) text = `${muscle}: −${plural(-applied)} — ${dec.why}.`;
    else if (dec.delta > 0) text = `${muscle}: sets held at ${total}, the most this meso allows — ${dec.why}.`;
    else text = `${muscle}: sets held — ${dec.why}.`;
    if (carries && dec.delta < 0 && applied === 0) text = `${muscle}: sets held at the minimum — ${dec.why}. If this keeps up, deload early.`;
    const lead = list[0].pe;
    say(lead, 'sets', text, list.reduce((a, x) => a + x.n, 0), total, { muscle, delta: carries ? applied : 0, feedback: list[0].fb, perf });
    for (const x of list) {
      const st = { ...(x.pe.schemeState || {}) };
      if (carries) {
        st.adj = (x.n + x.share) - (x.mev + nextWk.week);
        st.adjCycle = wk.cycle;
      }
      st.fb = { week: wk.index, delta: carries ? x.share : 0, reason: text.replace(/^[^:]+:\s*/, '').replace(/\.$/, '') };
      if (dec.flag) {
        st.joint = 'bad';
        say(x.pe, 'flag', `${x.pe.name}: joint pain flagged — sets held. Swap it for a variation that doesn't hurt.`, null, null, { muscle });
      } else if (st.joint) delete st.joint;
      updates.set(x.pe.id, st);
    }
  }
  return day.exercises.map(pe => (updates.has(pe.id) ? { ...pe, schemeState: updates.get(pe.id) } : pe));
}

// ——— Authoring: schemes, cycles, previews, presets ———————————————————————————

// The editor's "Progression" picker, in order.
export const SCHEME_PICKER = [
  { id: 'double',     label: 'Double progression',     sub: 'Fill the rep range, then add weight' },
  { id: 'linear',     label: 'Linear',                 sub: 'Same sets × reps, add weight every session' },
  { id: 'percent',    label: '% of training max wave', sub: 'Waves of percentages, + sets move the max' },
  { id: 'rpe_topset', label: 'Top set + back-offs (RPE)', sub: 'Heavy top set by feel, back-offs follow it' },
  { id: 'gzcl',       label: 'Tiered (GZCL)',          sub: 'Rep scheme steps down when you miss' },
  { id: 'rp_sets',    label: 'Mesocycle (RP-style)',   sub: 'RIR 3→0, sets from your feedback, deload' },
];

// Waves the editor offers for '% of training max'.
export const WAVE_PICKER = ['w531', 'fsl', 'bbb', 'rtf', 'block6', 'peak12', 'dupHeavy', 'dupMedium', 'dupLight', 'txVolume', 'txIntensity', 'pyrHeavy', 'pyrT2'];

// How a training max moves (cfg.tmRule overrides the wave's default).
export const TM_RULES = [
  { id: 'cycle',   label: 'Once a wave',   sub: 'Up when the + set makes its reps; down 10 % when it misses' },
  { id: 'amrap',   label: 'Every session', sub: 'From the reps on the + set' },
  { id: 'rtf',     label: 'Rep-out target', sub: 'Up or down by reps vs the target' },
  { id: 'success', label: 'All reps made', sub: 'Up when every set is made' },
  { id: 'test',    label: 'Test week',     sub: '90 % of the new max on the test week' },
  { id: 'none',    label: 'Fixed',         sub: 'Never changes (supplemental work)' },
];

// Increment choices per unit, as nominal lb (incFor maps them to 1.25 / 2.5 / 5 kg).
export const INC_CHOICES = [2.5, 5, 10];
export const incLabel = (lbInc, units = 'lb') => (units === 'kg' ? `${{ 2.5: 1.25, 5: 2.5, 10: 5 }[lbInc] ?? fmtW(incFor(lbInc, 'kg'), 'kg')} kg` : `${lbInc} lb`);

const LOWER = new Set(['Quads', 'Hamstrings', 'Glutes', 'Lower Back', 'Adductors', 'Abductors']);
const isLower = (pe) => LOWER.has(pe?.group) || /deadlift|squat|leg press|hip thrust/i.test(pe?.name || '');

// Sensible starting config when the user switches a lift to `scheme`.
export function defaultSchemeCfg(scheme, pe = {}) {
  const small = isSmall(pe);
  const lower = isLower(pe);
  if (scheme === 'linear') return { inc: lower ? (/deadlift/i.test(pe.name || '') ? 10 : 5) : small ? 2.5 : 5, failLimit: 3 };
  if (scheme === 'percent') return { wave: 'w531', inc: lower ? 10 : 5 };
  if (scheme === 'rpe_topset') return { topReps: Math.min(5, Math.max(1, pe.repMin || 3)), rpe: [7, 8, 9, 6], backoffs: 3, backoffReps: Math.max(3, Math.min(8, pe.repMax || 5)), drop: 10 };
  if (scheme === 'gzcl') return { tier: small ? 3 : 1, inc: lower ? 10 : 5 };
  if (scheme === 'rp_sets') { const mev = Math.max(2, Math.min(4, (pe.sets || 3) - 1)); return { mev, mrv: mev + 3, rirStart: 3, rirEnd: 0 }; }
  return small ? { inc: 2.5 } : {};
}

// Switch a program exercise to a scheme (cfg merged over the defaults). Keeps
// sets / repMin / repMax consistent with what the scheme prescribes, so stats,
// volume and the live workout agree. Progress state resets except an e1RM.
export function withScheme(pe, scheme, cfg = null) {
  const s = SCHEMES[scheme] ? scheme : 'double';
  const c = { ...defaultSchemeCfg(s, pe), ...(cfg || {}) };
  const next = { ...pe };
  if (s === 'double') {
    delete next.scheme; delete next.schemeState;
    const inc = cfg?.inc ?? pe.schemeCfg?.inc;
    const keep = inc && inc !== (isSmall(pe) ? 2.5 : 5) ? { inc } : null;
    if (keep) next.schemeCfg = keep; else delete next.schemeCfg;
    return next;
  }
  next.scheme = s;
  next.schemeCfg = c;
  next.schemeState = pe.scheme === s ? { ...(pe.schemeState || {}) } : (pe.schemeState?.e1rm ? { e1rm: pe.schemeState.e1rm } : {});
  if (s === 'linear') { next.sets = c.sets || pe.sets || 3; delete next.schemeCfg.sets; next.repMax = next.repMin = c.reps || pe.repMin || 5; delete next.schemeCfg.reps; }
  if (s === 'percent') {
    const w = WAVES[c.wave] || WAVES.w531;
    next.sets = Math.max(...w.weeks.map(wk => wk.reduce((a, sp) => a + (sp.sets || 1), 0)));
    const reps = w.weeks[0].map(sp => sp.reps);
    next.repMin = Math.min(...reps); next.repMax = Math.max(...reps);
  }
  if (s === 'rpe_topset') { next.sets = 1 + (c.backoffs ?? 3); next.repMin = Math.min(c.topReps, c.backoffReps || c.topReps); next.repMax = Math.max(c.topReps, c.backoffReps || c.topReps); }
  if (s === 'gzcl') { const st = GZ[[1, 2, 3].includes(c.tier) ? c.tier : 1][0]; next.sets = st.sets; next.repMin = next.repMax = st.reps; }
  if (s === 'rp_sets') {
    next.sets = c.mev;
    if (!(pe.repMax > 5)) { next.repMin = 8; next.repMax = 12; }
  }
  return next;
}

// Week labels for a cycle given the program's dominant scheme.
function autoLabels(n, deloadLast, kind, rirStart = 3, rirEnd = 0) {
  const acc = deloadLast ? n - 1 : n;
  const rir = rirRamp(acc, rirStart, rirEnd);
  const w531 = ['5s', '3s', '5/3/1'];
  return Array.from({ length: n }, (_, i) => {
    if (deloadLast && i === n - 1) return { label: 'Deload', deload: true };
    if (kind === 'rp_sets') return { label: `Week ${i + 1} · ${rir[i]} RIR` };
    if (kind === 'w531' && acc === 3) return { label: `Week ${i + 1} · ${w531[i]}` };
    return { label: `Week ${i + 1}` };
  });
}

// The program's dominant engine scheme (for labels and the cycle builder).
export function dominantScheme(program) {
  const count = {};
  for (const d of program?.days || []) for (const pe of d.exercises || []) {
    const s = schemeOf(pe);
    const k = s === 'percent' && pe.schemeCfg?.wave === 'w531' ? 'w531' : s;
    if (s !== 'double') count[k] = (count[k] || 0) + 1;
  }
  return Object.entries(count).sort((a, b) => b[1] - a[1])[0]?.[0] || 'double';
}

// Build a cycle: { weeks: n, deload: 'last' | 'none', repeat: true | false }.
export function makeCycle({ weeks = 4, deload = 'last', repeat = true } = {}, program = null) {
  const n = Math.max(1, Math.min(16, weeks | 0));
  const deloadLast = deload === 'last' && n > 1;
  const kind = dominantScheme(program);
  let rs = 3, re = 0;
  for (const d of program?.days || []) for (const pe of d.exercises || []) if (schemeOf(pe) === 'rp_sets') { rs = pe.schemeCfg?.rirStart ?? 3; re = pe.schemeCfg?.rirEnd ?? 0; }
  return { repeat: !!repeat, weeks: autoLabels(n, deloadLast, kind, rs, re) };
}

// Read a cycle back into builder fields.
export function cycleFields(program) {
  const c = program?.cycle;
  if (!c || !Array.isArray(c.weeks) || !c.weeks.length) return { weeks: 1, deload: 'none', repeat: true, custom: false };
  const n = c.weeks.length;
  const last = c.weeks[n - 1];
  const deloadLast = !!last?.deload;
  const others = c.weeks.slice(0, -1).some(w => w.deload);
  return { weeks: n, deload: deloadLast ? 'last' : 'none', repeat: c.repeat !== false, custom: others };
}

// The next `count` weeks of prescriptions for one exercise, from the absolute
// week `from`. → [{ index, week, label, deload, rows, text, note, sets }]
export function previewWeeks({ program, pe, from = 0, count = 4, units = 'lb', history = null } = {}) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const idx = from + i;
    const info = weekInfo(program, idx);
    if (info.finished && i > 0) break;
    const rx = prescribe({ program, pe, weekIndex: idx, units, history });
    out.push({
      index: idx, week: info.week, cycle: info.cycle, label: info.label, deload: info.deload,
      rows: rx.rows, text: rowsSummary(rx.rows, units, { weights: !rx.needsTM }), note: rx.note, sets: rx.rows.length, needsTM: !!rx.needsTM,
    });
  }
  return out;
}

const mapExercises = (program, fn) => ({ ...program, days: (program.days || []).map((d, di) => ({ ...d, exercises: (d.exercises || []).map((pe, i) => fn(pe, i, d, di)) })) });

// Is this program exercise a main barbell compound (squat / bench / deadlift /
// press / row family)? `ex` is its library entry when known.
export function isMainLift(pe, ex = null) {
  const name = `${pe?.name || ''} ${ex?.name || ''}`;
  if (pe?.unit === 'sec') return false;
  if (/curl|raise|fly|flye|extension|pushdown|kickback|crunch|plank|shrug|calf/i.test(name)) return false;
  if (ex) return ex.equipment === 'Barbell' && ex.mechanic === 'compound';
  return /squat|bench press|deadlift|overhead press|military press|barbell row/i.test(name);
}

// Presets. Each → { program, summary } and keeps the days, names and order.
export const PRESETS = [
  { id: 'wave531', label: 'Convert to 5/3/1 wave', sub: 'Main barbell lift of each day runs 5s / 3s / 5-3-1+ / deload on a training max' },
  { id: 'meso', label: 'Convert to hypertrophy meso', sub: 'RIR 3→0 over 4 weeks, sets from your feedback, then a deload' },
  { id: 'linear', label: 'Linear novice', sub: 'Compounds 3×5, add weight every session; the rest fills its rep range' },
];

export function applyPreset(program, id, { exercises = [] } = {}) {
  const exOf = (pe) => exercises.find(e => e.id === pe.exerciseId) || null;
  if (id === 'wave531') {
    let n = 0;
    const seen = new Set();
    const p = mapExercises(program, (pe, i, d) => {
      // One main lift per day: the first barbell compound (else the first exercise).
      const first = (d.exercises || []).findIndex(x => isMainLift(x, exOf(x)));
      const pick = first >= 0 ? first : 0;
      if (i !== pick) return schemeOf(pe) === 'percent' ? withScheme(pe, 'double') : pe;
      n++; seen.add(pe.name);
      return withScheme(pe, 'percent', { wave: 'w531', inc: isLower(pe) ? 10 : 5 });
    });
    p.cycle = { repeat: true, weeks: autoLabels(4, true, 'w531') };
    return { program: p, summary: `${n} main lift${n === 1 ? '' : 's'} on a 4-week wave: ${[...seen].join(', ')}. Set a training max for each.` };
  }
  if (id === 'meso') {
    let n = 0;
    const p = mapExercises(program, (pe) => {
      if (pe.unit === 'sec') return pe;
      n++;
      const base = withScheme({ ...pe, repMin: pe.repMin >= 6 ? pe.repMin : 6, repMax: pe.repMax >= 8 ? pe.repMax : 10 }, 'rp_sets', null);
      return base;
    });
    p.cycle = { repeat: true, weeks: autoLabels(5, true, 'rp_sets') };
    return { program: p, summary: `${n} exercises on a 4-week meso (RIR 3→0) plus a deload. Rate pump, soreness and joints after each workout to steer the sets.` };
  }
  if (id === 'linear') {
    let n = 0;
    const p = mapExercises(program, (pe) => {
      if (isMainLift(pe, exOf(pe))) { n++; return withScheme({ ...pe, sets: 3, repMin: 5, repMax: 5 }, 'linear', { failLimit: 3 }); }
      return schemeOf(pe) === 'double' ? pe : withScheme(pe, 'double');
    });
    delete p.cycle;
    return { program: p, summary: n ? `${n} compound lift${n === 1 ? '' : 's'} at 3×5, adding weight every session (three misses → 10 % reset).` : 'No barbell compounds found: nothing moved to linear.' };
  }
  return { program, summary: '' };
}

// ——— Labels and summaries for the UI ——————————————————————————————————————

export function schemeLabel(pe, units = 'lb') {
  const s = schemeOf(pe);
  const cfg = pe?.schemeCfg || {};
  if (s === 'linear') return `Linear +${fmtW(incFor(defaultInc(pe), units), units)} ${U(units)} a session`;
  if (s === 'percent') return `${WAVES[cfg.wave]?.label || 'Percent block'} · % of training max`;
  if (s === 'rpe_topset') {
    const r = Array.isArray(cfg.rpe) ? `${Math.min(...cfg.rpe.filter(x => x > 6))}→${Math.max(...cfg.rpe)}` : (cfg.rpe ?? 8);
    return `Top set of ${cfg.topReps || pe.repMin || 3} at RPE ${r} + ${cfg.backoffs ?? 3} back-offs at −${cfg.drop ?? 10}%`;
  }
  if (s === 'gzcl') { const t = gzTier(pe); return `Tier ${t} · ${GZ[t][gzStage(pe)].label}`; }
  if (s === 'rp_sets') return `Meso: ${cfg.mev || pe.sets || 2}→${cfg.mrv || (cfg.mev || pe.sets || 2) + 3} sets, RIR 3→0`;
  return 'Double progression';
}

// Compact text for a prescription's rows, e.g. "65%×5 · 75%×5 · 85%×5+" or "3×5 @ 225".
export function rowsSummary(rows, units = 'lb', { weights = true } = {}) {
  if (!rows || !rows.length) return '';
  // "5×3+": identical sets where only the last is a + set.
  const last = rows[rows.length - 1];
  if (rows.length > 1 && last.amrap && !last.top && rows.every(r => r.reps === last.reps && r.w === last.w && r.pct === last.pct && !r.backoff && (r === last || !r.amrap))) {
    const at = r => (r.pct > 0 ? ` @ ${r.pct}%${weights && r.w ? ` (${fmtW(r.w, units)})` : ''}` : (weights && r.w ? ` @ ${fmtW(r.w, units)}` : ''));
    return `${rows.length}×${last.reps}+${at(last)}${last.target > last.reps ? ` · last set to ${last.target}` : ''}`;
  }
  const groups = [];
  for (const r of rows) {
    const key = [r.pct, r.reps, r.repsMax, r.amrap, r.target, r.w, r.rpe, r.top].join('|');
    const g = groups[groups.length - 1];
    if (g && g.key === key) g.n++; else groups.push({ key, r, n: 1 });
  }
  return groups.map(({ r, n }) => {
    // A meso row with a load is a target ("3×10 @ 130 · 1 RIR"); without one it is the range.
    const target = r.rir != null && weights && r.w > 0;
    const reps = r.repsMax && r.repsMax !== r.reps && !target ? `${r.reps}–${r.repsMax}` : `${r.reps}${r.amrap ? '+' : ''}`;
    const head = `${n}×${reps}`;
    if (r.pct != null && r.pct > 0) {
      return n > 1 ? `${n}×${reps} @ ${r.pct}%${weights && r.w ? ` (${fmtW(r.w, units)})` : ''}` : `${r.pct}%×${reps}${weights && r.w ? ` (${fmtW(r.w, units)})` : ''}`;
    }
    const at = weights && r.w ? ` @ ${fmtW(r.w, units)}` : '';
    if (r.top) return at ? `top ${reps}${at}, RPE ${r.rpe}` : `top ${reps} @ RPE ${r.rpe}`;
    if (r.backoff) return at ? `${head}${at}` : `${head} at −${-r.pct}%`;
    return `${head}${at}${r.rir != null ? ` · ${r.rir} RIR` : ''}`;
  }).join(' · ');
}

// The whole cycle for one exercise: [{ week, label, deload, rows, text }].
export function weekTable(program, pe, units = 'lb') {
  const { weeks } = cycleOf(program);
  return weeks.map((w, i) => {
    const rx = prescribe({ program, pe, weekIndex: i, units });
    return { week: i, label: w.label || `Week ${i + 1}`, deload: !!w.deload, rows: rx.rows, text: rowsSummary(rx.rows, units) };
  });
}

// Does this program use anything beyond double progression?
export const usesEngine = (program) => (program?.days || []).some(d => (d.exercises || []).some(pe => schemeOf(pe) !== 'double'));
